import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { PAGE_META, usePageMeta } from '../lib/pageMeta';
import { validatePhone, validateEmail } from '../lib/phoneEmailValidation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import CourseSearchSelect from './ui/CourseSearchSelect';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  UserIcon,
  CurrencyRupeeIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  TrashIcon,
  DocumentIcon,
  PhotoIcon,
  ArrowPathIcon,
  DocumentDuplicateIcon,
  InformationCircleIcon,
  CalendarIcon,
  XCircleIcon,
  NoSymbolIcon,
  AcademicCapIcon,
  BanknotesIcon,
  ArrowTopRightOnSquareIcon,
  ChartBarIcon,
  FunnelIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { getApiErrorMessage } from '../lib/apiError';
import { downloadCsv, formatDateForCsv } from '../lib/csvExport';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Function to get token from localStorage or wherever you store it
const getToken = () => {
  return localStorage.getItem('admin_token');
};

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include token
api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    error.userMessage = getApiErrorMessage(error, 'An unexpected error occurred');
    return Promise.reject(error);
  }
);

// Document types configuration
const DOCUMENT_TYPES = [
  // { value: 'id', label: 'ID Card' },
  // { value: '10th-certificate', label: '10th Certificate' },
  // { value: '12th-certificate', label: '12th Certificate' },
  // { value: 'degree-certificate', label: 'Degree Certificate' },
  { value: 'photo', label: 'Photo' },
  { value: 'adhaar', label: 'Aadhaar Card' },
  { value: 'others', label: 'Other Documents' }
  // { value: 'pancard', label: 'PAN Card' }
];

// ===================== SKELETON LOADERS =====================

const SkeletonPulse = ({ className = "", delay = "0s" }) => (
  <div
    className={`animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%] rounded ${className}`}
    style={{
      animation: `shimmer 2s ease-in-out ${delay} infinite`,
      backgroundSize: '200% 100%'
    }}
  />
);

// Dashboard-style stat card skeleton (matches Dashboard.jsx StatCardSkeleton)
const StatsCardSkeleton = ({ color = "blue" }) => {
  const iconGrad = {
    blue:   "from-blue-400 to-blue-600",
    green:  "from-green-400 to-green-600",
    violet: "from-violet-400 to-violet-600",
    orange: "from-orange-400 to-orange-600",
    red:    "from-red-400 to-red-600",
  };
  return (
    <Card className="overflow-hidden relative">
      <CardContent className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex-1 space-y-3">
            <div className="h-4 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%]" />
            <div className="h-8 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-lg animate-pulse-scale" />
            <div className="h-3 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%] delay-75" />
          </div>
          <div className={`p-3 rounded-full bg-gradient-to-br ${iconGrad[color]} opacity-20 animate-float`}>
            <div className="w-6 h-6 bg-white/50 rounded" />
          </div>
        </div>
        <div className="flex items-center mt-4 space-x-2 animate-slide-in-left">
          <div className="h-4 w-4 bg-green-200 rounded animate-bounce" />
          <div className="h-3 w-16 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full" />
          <div className="h-3 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full" />
        </div>
        <div className="absolute top-2 right-2 w-2 h-2 bg-blue-200 rounded-full animate-ping" />
        <div className="absolute bottom-4 left-4 w-1 h-1 bg-violet-200 rounded-full animate-ping delay-300" />
      </CardContent>
    </Card>
  );
};

// Dashboard-style table skeleton
const TableSkeleton = () => (
  <div className="space-y-0">
    {/* Header */}
    <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-500 to-violet-500 rounded-t-lg">
      {[8, 48, 56, 28, 28, 28, 24, 32].map((w, i) => (
        <div
          key={i}
          className="h-3.5 bg-white/30 rounded-full flex-shrink-0 animate-shimmer bg-[length:200%_100%]"
          style={{ width: `${w}px`, animationDelay: `${i * 60}ms` }}
        />
      ))}
    </div>
    {/* Rows */}
    {[0, 1, 2, 3, 4, 5, 6].map((row) => (
      <div
        key={row}
        className="flex items-center gap-3 px-4 py-3.5 border-b border-gray-100 bg-white relative overflow-hidden"
        style={{ animationDelay: `${row * 80}ms` }}
      >
        {/* shimmer sweep */}
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-shimmer-slow bg-[length:200%_100%]" />
        {/* # */}
        <div className="w-5 h-3 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%] flex-shrink-0" />
        {/* Student info */}
        <div className="flex-1 space-y-1.5 min-w-0">
          <div className="h-3.5 w-28 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%]" />
          <div className="h-2.5 w-40 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%] delay-75" />
          <div className="h-2.5 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%] delay-100" />
        </div>
        {/* Course badge */}
        <div className="w-24 h-5 bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 rounded-full animate-pulse-scale flex-shrink-0" />
        {/* Amounts */}
        <div className="w-20 h-3.5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full animate-shimmer bg-[length:200%_100%] flex-shrink-0" />
        <div className="w-20 h-3.5 bg-gradient-to-r from-green-100 via-green-200 to-green-100 rounded-full animate-shimmer bg-[length:200%_100%] delay-75 flex-shrink-0" />
        <div className="w-20 h-3.5 bg-gradient-to-r from-orange-100 via-orange-200 to-orange-100 rounded-full animate-shimmer bg-[length:200%_100%] delay-100 flex-shrink-0" />
        {/* Status */}
        <div className="w-16 h-5 bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 rounded-full animate-pulse-scale flex-shrink-0" />
        {/* Buttons */}
        <div className="flex gap-1.5 flex-shrink-0">
          <div className="h-7 w-7 bg-gradient-to-br from-blue-100 to-blue-200 rounded animate-float" />
          <div className="h-7 w-7 bg-gradient-to-br from-violet-100 to-violet-200 rounded animate-float delay-100" />
          <div className="h-7 w-7 bg-gradient-to-br from-red-100 to-red-200 rounded animate-float delay-200" />
        </div>
      </div>
    ))}
  </div>
);

// Transfer Preview Skeleton
const TransferPreviewSkeleton = () => (
  <div className="space-y-4 p-4">
    <div className="flex items-center justify-between">
      <div className="h-6 w-32 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-[shimmer_2s_ease-in-out_infinite]" />
      <div className="h-6 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-[shimmer_2s_ease-in-out_0.2s_infinite]" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="p-3 border rounded-lg space-y-2">
          <div className="h-4 w-20 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-[shimmer_2s_ease-in-out_infinite]" />
          <div className="h-6 w-24 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-[shimmer_2s_ease-in-out_0.3s_infinite]" />
        </div>
      ))}
    </div>
  </div>
);

// File upload skeleton
const FileUploadSkeleton = ({ type }) => (
  <div className="flex items-center justify-between p-3 border-2 border-blue-200 rounded-lg bg-blue-50 animate-[pulse_2s_ease-in-out_infinite]">
    <div className="flex items-center space-x-3 flex-1">
      <div className="h-5 w-5 bg-gradient-to-br from-blue-300 to-blue-400 rounded animate-[spin_3s_linear_infinite]" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-32 bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200 rounded animate-[shimmer_2s_ease-in-out_infinite]" />
        <div className="h-2 w-48 bg-gradient-to-r from-blue-100 via-blue-200 to-blue-100 rounded animate-[shimmer_2s_ease-in-out_0.3s_infinite]" />
      </div>
    </div>
    <div className="flex items-center gap-2">
      <div className="h-2 w-24 bg-gradient-to-r from-blue-300 via-blue-400 to-blue-300 rounded-full overflow-hidden">
        <div className="h-full w-1/2 bg-gradient-to-r from-blue-500 to-violet-500 animate-[loading_1.5s_ease-in-out_infinite]"
          style={{ animation: 'loading 1.5s ease-in-out infinite' }} />
      </div>
      <span className="text-xs text-blue-600 font-medium animate-[pulse_1s_ease-in-out_infinite]">
        Uploading...
      </span>
    </div>
  </div>
);

// Batch selection skeleton
const BatchSkeletonLoader = () => (
  <div className="space-y-2">
    {[1, 2, 3].map((i) => (
      <div
        key={i}
        className="p-4 border-2 border-gray-200 rounded-lg animate-[expand_2s_ease-in-out_infinite]"
        style={{
          animation: `expand 2s ease-in-out ${i * 0.2}s infinite`,
          animationDelay: `${i * 0.2}s`
        }}
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 space-y-3">
            <div className="flex items-center gap-2">
              <div className="h-5 w-32 bg-gradient-to-r from-blue-200 via-blue-300 to-blue-200 rounded animate-[shimmer_2s_ease-in-out_infinite]" />
              <div className="h-5 w-16 bg-gradient-to-r from-violet-200 via-violet-300 to-violet-200 rounded-full animate-[expand_1.5s_ease-in-out_infinite]" />
              <div className="h-5 w-24 bg-gradient-to-r from-green-200 via-green-300 to-green-200 rounded-full animate-[expand_1.5s_ease-in-out_0.3s_infinite]" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="h-4 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-[shimmer_2s_ease-in-out_infinite]" />
              <div className="h-4 w-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded animate-[shimmer_2s_ease-in-out_0.2s_infinite]" />
            </div>
          </div>
        </div>
        {/* Progress bar */}
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-blue-500 via-violet-500 to-blue-500 animate-[progress_2s_ease-in-out_infinite]"
            style={{
              animation: 'progress 2s ease-in-out infinite',
              width: '60%'
            }} />
        </div>
      </div>
    ))}
  </div>
);

// Add CSS animations to your global styles or in a <style> tag
const AnimationStyles = () => (
  <style>{`
    @keyframes shimmer {
      0%, 100% { background-position: 200% 0; }
      50%       { background-position: -200% 0; }
    }
    @keyframes shimmer-slow {
      0%, 100% { background-position: 200% 0; }
      50%       { background-position: -200% 0; }
    }
    @keyframes pulse-scale {
      0%, 100% { transform: scale(1);    opacity: 0.8; }
      50%       { transform: scale(1.04); opacity: 1;   }
    }
    @keyframes float {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-8px); }
    }
    @keyframes expand {
      0%, 100% { transform: scale(1);    opacity: 0.7; }
      50%       { transform: scale(1.05); opacity: 1;   }
    }
    @keyframes bubble {
      0%, 100% { transform: scale(1)    translateY(0);   }
      50%       { transform: scale(1.1) translateY(-2px); }
    }
    @keyframes wave {
      0%, 100% { transform: translateX(-100%); }
      50%       { transform: translateX(100%); }
    }
    @keyframes fadeSlide {
      0%, 100% { opacity: 0.5; transform: translateX(-5px); }
      50%       { opacity: 1;   transform: translateX(0);    }
    }
    @keyframes slideIn {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0);    }
    }
    @keyframes slide-in-left {
      from { transform: translateX(-20px); opacity: 0; }
      to   { transform: translateX(0);     opacity: 1; }
    }
    @keyframes spin {
      from { transform: rotate(0deg); }
      to   { transform: rotate(360deg); }
    }
    @keyframes progress {
      0%, 100% { width: 30%; }
      50%       { width: 70%; }
    }
    @keyframes loading {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(200%);  }
    }
    .animate-shimmer       { animation: shimmer 2s ease-in-out infinite; }
    .animate-shimmer-slow  { animation: shimmer-slow 3s ease-in-out infinite; }
    .animate-pulse-scale   { animation: pulse-scale 2s ease-in-out infinite; }
    .animate-float         { animation: float 3s ease-in-out infinite; }
    .animate-slide-in-left { animation: slide-in-left 0.5s ease-out; }
    .delay-75  { animation-delay: 75ms;  }
    .delay-100 { animation-delay: 100ms; }
    .delay-200 { animation-delay: 200ms; }
    .delay-300 { animation-delay: 300ms; }
  `}</style>
);

// ===================== END SKELETON LOADERS =====================

const EMPTY_DRAFT = { search: '', course: 'all', status: 'all', fromDate: '', toDate: '' };
const PAGE_SIZE = 20;

export default function StudentManagement() {
  usePageMeta(PAGE_META.students);
  const navigate = useNavigate();
  const currentUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const isCounselor = currentUser?.role === 'counselor';
  const [studentsList, setStudentsList] = useState([]);
  const [globalStats, setGlobalStats] = useState({ totalStudents: 0, activeStudents: 0, discontinuedCount: 0, totalRevenue: 0, totalPendingAmount: 0 });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalStudents: 0 });
  const [courses, setCourses]               = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [showEnrollSuccess, setShowEnrollSuccess] = useState(false);
  const enrollCloseTimer = useRef(null);
  const [isTransferDialogOpen, setIsTransferDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [transferAvailableBatches, setTransferAvailableBatches] = useState({
    sameCourseBatches: [],
    otherCourseBatches: []
  });
  const [transferPreview, setTransferPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState({});
  const [deletingDocType, setDeletingDocType] = useState(null);
  const [loadingBatches, setLoadingBatches] = useState(false);
  const [transferHistory, setTransferHistory] = useState(null);
  const [transferHistoryLoading, setTransferHistoryLoading] = useState(false);
  // Draft = what user is typing; applied = what gets sent to backend
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [applied, setApplied] = useState(EMPTY_DRAFT);
  const [page, setPage] = useState(1);
  const [filterOpen, setFilterOpen] = useState(false);

  // Convenience aliases used by the rest of the component (filter chips, etc.)
  const searchTerm           = applied.search;
  const selectedCourseFilter = applied.course;
  const statusFilter         = applied.status;
  const startDateFilter      = applied.fromDate;
  const endDateFilter        = applied.toDate;

  const [newStudent, setNewStudent] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    admissionSource: 'website',
    documents: [],
    status: 'active'
  });

  const [enrollmentData, setEnrollmentData] = useState({
    courseId: '',
    batchId: '',
    paymentPlan: 'full',
    numInstallments: 2,
    installmentAmounts: [],
    amountPaid: '',
    customTotalAmount: '',
    discountReason: ''
  });

  const [transferData, setTransferData] = useState({
    fromBatchId: '',
    toCourseId: '',
    toBatchId: '',
    transferReason: '',
    feeAdjustmentType: 'transfer_fee',
    numInstallments: 1,
    notes: ''
  });

  // ── Discontinue state ──────────────────────────────────────────────────────
  const [isDiscontinueDialogOpen, setIsDiscontinueDialogOpen] = useState(false);
  const [discontinueLoading, setDiscontinueLoading] = useState(false);
  const [discontinuePreview, setDiscontinuePreview] = useState(null);
  const [discontinuePreviewLoading, setDiscontinuePreviewLoading] = useState(false);
  const [discontinueData, setDiscontinueData] = useState({
    batchId: '',
    reason: '',
    refundType: 'no_refund',
    refundAmount: '',
    refundMode: 'cash',
    referenceNo: '',
    notes: ''
  });

  // Fetch students and courses on component mount / when applied filters change
  useEffect(() => {
    fetchStudents();
  }, [applied, page]); // eslint-disable-line

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit: PAGE_SIZE,
        ...(applied.search   && { search:   applied.search }),
        ...(applied.course   && applied.course !== 'all' && { course:   applied.course }),
        ...(applied.status   && applied.status !== 'all' && { status:   applied.status }),
        ...(applied.fromDate && { fromDate: applied.fromDate }),
        ...(applied.toDate   && { toDate:   applied.toDate }),
      };
      const response = await api.get('/student-dashboard/with-enrollments', { params });

      if (response.data.success) {
        setStudentsList(response.data.data.students || []);
        setPagination(response.data.data.pagination || { currentPage: 1, totalPages: 1, totalStudents: 0 });
        if (response.data.data.statistics) {
          setGlobalStats(response.data.data.statistics);
        }
      } else {
        toast.error('Failed to load students');
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load students'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      setCoursesLoading(true);
      const response = await api.get('/student-dashboard/courses');
      if (response.data.success) {
        setCourses(response.data.data || []);
      } else {
        toast.error('Failed to load courses');
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load courses'));
    } finally {
      setCoursesLoading(false);
    }
  };

  const fetchBatchesForCourse = async (courseId) => {
    try {
      setLoadingBatches(true);
      const response = await api.get(`/student-dashboard/courses/${courseId}/batches`);

      if (response.data.success) {
        setAvailableBatches(response.data.data || []);
      } else {
        toast.error('Failed to load batches');
        setAvailableBatches([]);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load batches'));
      setAvailableBatches([]);
    } finally {
      setLoadingBatches(false);
    }
  };

  const fetchTransferBatches = async (courseId) => {
    try {
      setLoadingBatches(true);
      const response = await api.get(`/student-dashboard/courses/${courseId}/batches`);

      if (response.data.success) {
        // For destination batches, we just need a simple array
        setTransferAvailableBatches({
          sameCourseBatches: response.data.data || [],
          otherCourseBatches: []
        });
      } else {
        toast.error('Failed to load batches');
        setTransferAvailableBatches({
          sameCourseBatches: [],
          otherCourseBatches: []
        });
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load batches'));
      setTransferAvailableBatches({
        sameCourseBatches: [],
        otherCourseBatches: []
      });
    } finally {
      setLoadingBatches(false);
    }
  };

  // Fetch available batches for transfer
  const fetchAvailableBatchesForTransfer = async () => {
    try {
      setLoadingBatches(true);
      const response = await api.get('/student-transfer/available-batches', {
        params: {
          studentId: selectedStudent?._id,
          currentBatchId: selectedStudent?.enrolledCourses?.[0]?.batchId
        }
      });

      if (response.data.success) {
        // Store both same course and other course batches
        setTransferAvailableBatches(response.data.data || {
          sameCourseBatches: [],
          otherCourseBatches: []
        });
      }
    } catch (error) {
      console.error('Error fetching available batches:', error);
      toast.error(getApiErrorMessage(error, 'Failed to load available batches'));
      setTransferAvailableBatches({
        sameCourseBatches: [],
        otherCourseBatches: []
      });
    } finally {
      setLoadingBatches(false);
    }
  };

  // Calculate transfer preview
  const calculateTransferPreview = async () => {
    if (!selectedStudent || !transferData.toCourseId || !transferData.toBatchId) {
      toast.error('Please select destination course and batch');
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await api.post('/student-transfer/calculate-preview', {
        studentId: selectedStudent._id,
        fromBatchId: transferData.fromBatchId || undefined,
        toCourseId: transferData.toCourseId,
        toBatchId: transferData.toBatchId,
        feeAdjustmentType: transferData.feeAdjustmentType
      });

      if (response.data.success) {
        setTransferPreview(response.data.data);
      }
    } catch (error) {
      console.error('Error calculating transfer preview:', error);
      toast.error(getApiErrorMessage(error, 'Failed to calculate transfer preview'));
    } finally {
      setPreviewLoading(false);
    }
  };

  const fetchTransferHistory = async (studentId) => {
    setTransferHistoryLoading(true);
    try {
      const response = await api.get(`/student-transfer/history/${studentId}`);
      if (response.data.success) {
        setTransferHistory(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching transfer history:', error);
      setTransferHistory({ enrollmentHistory: [], pastEnrollments: [], totalTransfers: 0 });
    } finally {
      setTransferHistoryLoading(false);
    }
  };

  // File upload handler
  const handleFileUpload = async (file, documentType) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadingFiles(prev => ({ ...prev, [documentType]: true }));

      const response = await api.post('/storage/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.url) {
        const newDocument = {
          type: documentType,
          fileName: response.data.originalName,
          fileUrl: response.data.url,
          uploadedName: response.data.filename
        };

        setNewStudent(prev => ({
          ...prev,
          documents: [...prev.documents.filter(doc => doc.type !== documentType), newDocument]
        }));

        toast.success(`${DOCUMENT_TYPES.find(doc => doc.value === documentType)?.label} uploaded successfully!`);
        return newDocument;
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(
        getApiErrorMessage(
          error,
          `Failed to upload ${DOCUMENT_TYPES.find((doc) => doc.value === documentType)?.label}`
        )
      );
    } finally {
      setUploadingFiles(prev => ({ ...prev, [documentType]: false }));
    }
  };

  // File delete handler
  const handleFileDelete = async (document) => {
    if (!document.fileUrl) return;
    setDeletingDocType(document.type);
    try {
      const response = await api.delete('/storage/delete', {
        data: { uri: document.fileUrl }
      });

      if (response.data.message) {
        setNewStudent(prev => ({
          ...prev,
          documents: prev.documents.filter(doc => doc.type !== document.type)
        }));
        toast.success('File deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error(getApiErrorMessage(error, 'Failed to delete file'));
    } finally {
      setDeletingDocType(null);
    }
  };

  // File input change handler
  const handleFileChange = (event, documentType) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size should be less than 5MB');
      return;
    }

    handleFileUpload(file, documentType);
    event.target.value = ''; // Reset input
  };

  const handleViewStudent = (student) => {
    navigate(`/students/${student._id}`);
  };

  // ── Student History Dialog ────────────────────────────────────────────────
  const [isHistoryDialogOpen, setIsHistoryDialogOpen]   = useState(false);
  const [historyStudent, setHistoryStudent]             = useState(null);
  const [historyData, setHistoryData]                   = useState({ invoices: [], attendances: [], percentByBatch: [] });
  const [historyLoading, setHistoryLoading]             = useState(false);
  const [historyTab, setHistoryTab]                     = useState('overview');

  const fetchStudentHistory = async (student) => {
    setHistoryLoading(true);
    setHistoryData({ invoices: [], attendances: [], percentByBatch: [] });
    try {
      const [invoiceRes, attendanceRes] = await Promise.all([
        api.get(`/fees/invoices/${student._id}`),
        api.get(`/attendance/student/${student._id}`, { params: { limit: 500 } }),
      ]);
      setHistoryData({
        invoices:      invoiceRes.data?.invoices || [],
        attendances:   attendanceRes.data?.attendances || [],
        percentByBatch: attendanceRes.data?.percentageByBatch || [],
      });
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Failed to load student history'));
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleOpenHistory = (student) => {
    setHistoryStudent(student);
    setHistoryTab('overview');
    setIsHistoryDialogOpen(true);
    fetchStudentHistory(student);
  };

  const handleAddStudent = async (e) => {
    e.preventDefault();

    if (!newStudent.name || !newStudent.email || !newStudent.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    const phoneResult = validatePhone(newStudent.phone);
    if (!phoneResult.valid) { toast.error(phoneResult.message); return; }

    const emailResult = validateEmail(newStudent.email, { required: true });
    if (!emailResult.valid) { toast.error(emailResult.message); return; }

    try {
      setLoading(true);
      const response = await api.post('/students', {
        ...newStudent,
        phone: phoneResult.cleaned,
        email: emailResult.cleaned,
      });

      if (response.status === 201 || response.status === 200) {
        setStudentsList(prev => [...prev, response.data]);
        setNewStudent({
          name: '',
          email: '',
          phone: '',
          // guardian: { name: '', phone: '', email: '', relationship: '' },
          address: { street: '', city: '', state: '', pincode: '' },
          admissionSource: 'website',
          documents: [],
          status: 'active'
        });
        setIsAddDialogOpen(false);
        toast.success('Student added successfully!');
        fetchStudents(); // Refresh the list
      }
    } catch (error) {
      console.error('Error adding student:', error);
      const errorMessage = error.userMessage || error.response?.data?.message || 'Failed to add student';
      
      // Show more detailed error if available
      if (error.response?.data?.data?.error) {
        toast.error(error.response.data.data.error);
      } else {
        toast.error(errorMessage);
      }

      // Optional: Show field-specific errors if backend provides them
      if (error.response?.data?.errors) {
        Object.entries(error.response.data.errors).forEach(([field, message]) => {
          toast.error(`${field}: ${message}`);
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEnrollStudent = (student) => {
    setSelectedStudent(student);
    setIsEnrollDialogOpen(true);
  };

  // ── Discontinue handlers ────────────────────────────────────────────────────
  const handleDiscontinueStudent = (student) => {
    setSelectedStudent(student);
    const activeCourse = (student.enrolledCourses || []).find(c => !c.assignmentStatus || c.assignmentStatus === 'active');
    setDiscontinueData({
      batchId: activeCourse?.batchId || '',
      reason: '',
      refundType: 'no_refund',
      refundAmount: '',
      refundMode: 'cash',
      referenceNo: '',
      notes: ''
    });
    setDiscontinuePreview(null);
    setIsDiscontinueDialogOpen(true);
  };

  const fetchDiscontinuePreview = async (batchId, refundType) => {
    if (!selectedStudent || !batchId) return;
    setDiscontinuePreviewLoading(true);
    try {
      const res = await api.post('/student-discontinue/calculate-preview', {
        studentId: selectedStudent._id,
        batchId,
        refundType
      });
      setDiscontinuePreview(res.data.data);
      if (refundType !== 'no_refund' && res.data.data.suggestedRefund > 0) {
        setDiscontinueData(prev => ({ ...prev, refundAmount: res.data.data.suggestedRefund }));
      } else if (refundType === 'no_refund') {
        setDiscontinueData(prev => ({ ...prev, refundAmount: '' }));
      }
    } catch (err) {
      toast.error('Failed to load fee preview');
    } finally {
      setDiscontinuePreviewLoading(false);
    }
  };

  const handleDiscontinueSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !discontinueData.batchId || !discontinueData.reason.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (discontinueData.refundType !== 'no_refund') {
      const amt = parseFloat(discontinueData.refundAmount);
      if (!amt || amt <= 0) {
        toast.error('Please enter a valid refund amount');
        return;
      }
      if (!discontinueData.refundMode) {
        toast.error('Please select a refund mode');
        return;
      }
    }
    setDiscontinueLoading(true);
    try {
      const res = await api.post('/student-discontinue/discontinue', {
        studentId:    selectedStudent._id,
        batchId:      discontinueData.batchId,
        reason:       discontinueData.reason,
        refundType:   discontinueData.refundType,
        refundAmount: parseFloat(discontinueData.refundAmount) || 0,
        refundMode:   discontinueData.refundMode,
        referenceNo:  discontinueData.referenceNo,
        notes:        discontinueData.notes
      });
      if (res.data.success) {
        toast.success(`${selectedStudent.name} has been discontinued. Email sent.`);
        setIsDiscontinueDialogOpen(false);
        fetchStudents();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to discontinue student');
    } finally {
      setDiscontinueLoading(false);
    }
  };

  const handleDownloadDiscontinuationReport = async (student, batchId) => {
    try {
      const res = await api.get(`/student-discontinue/report/${student._id}/${batchId}`, {
        responseType: 'blob'
      });
      const url  = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href  = url;
      link.setAttribute('download', `discontinuation-report-${student.name.replace(/\s+/g,'-')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download report');
    }
  };

  const handleTransferStudent = (student) => {
    setSelectedStudent(student);

    // Pre-fill fromBatchId with the student's most-recently-enrolled active course.
    // We sort by enrollmentDate descending so the latest enrolment is the source.
    // (getAllStudentsWithEnrollments already filters to active assignments only.)
    const sortedCourses = [...(student.enrolledCourses || [])].sort(
      (a, b) => new Date(b.enrollmentDate) - new Date(a.enrollmentDate)
    );
    const sourceCourse = sortedCourses[0];
    setTransferData({
      ...transferData,
      fromBatchId: sourceCourse?.batchId || ''
    });

    // Fetch available batches for transfer
    fetchAvailableBatchesForTransfer();
    // Fetch transfer history
    fetchTransferHistory(student._id);

    setIsTransferDialogOpen(true);
  };

  const handleCourseChange = async (courseId) => {
    setEnrollmentData({
      ...enrollmentData,
      courseId: courseId,
      batchId: '',
      amountPaid: '',
      customTotalAmount: '',
      discountReason: ''
    });

    if (courseId) {
      await fetchBatchesForCourse(courseId);
    } else {
      setAvailableBatches([]);
    }
  };

  const handleTransferCourseChange = async (courseId) => {
    setTransferData({
      ...transferData,
      toCourseId: courseId,
      toBatchId: ''
    });
    setTransferPreview(null);

    if (courseId) {
      await fetchTransferBatches(courseId);
    } else {
      setTransferAvailableBatches([]);
    }
  };


  const isBatchFull = (batch) => {
    return batch.assignedStudents >= batch.capacity;
  };

  const handleEnrollmentSubmit = async (e) => {
    e.preventDefault();

    if (!selectedStudent || !enrollmentData.courseId || !enrollmentData.batchId) {
      toast.error('Please select course and batch');
      return;
    }

    try {
      setLoading(true);
      const finalFee = enrollmentData.customTotalAmount ? Number(enrollmentData.customTotalAmount) : applicableFee;

      // Validate installment amounts sum when EMI
      if (enrollmentData.paymentPlan === 'emi' && enrollmentData.installmentAmounts.length > 0) {
        const amtSum = enrollmentData.installmentAmounts.reduce((s, a) => s + (Number(a) || 0), 0);
        if (Math.abs(amtSum - finalFee) > 1) {
          toast.error(`Installment amounts must total ₹${finalFee.toLocaleString()} (current: ₹${amtSum.toLocaleString()})`);
          return;
        }
      }

      const enrollmentPayload = {
        studentId: selectedStudent._id,
        batchId: enrollmentData.batchId,
        paymentPlan: enrollmentData.paymentPlan,
        mode: selectedBatch?.mode || 'offline',
      };

      if (enrollmentData.paymentPlan === 'emi') {
        enrollmentPayload.numInstallments = enrollmentData.numInstallments;
        if (enrollmentData.installmentAmounts.length === enrollmentData.numInstallments) {
          enrollmentPayload.installmentAmounts = enrollmentData.installmentAmounts.map(Number);
        }
      }
      if (enrollmentData.customTotalAmount) {
        enrollmentPayload.customTotalAmount = Number(enrollmentData.customTotalAmount);
      }
      if (enrollmentData.discountReason) {
        enrollmentPayload.discountReason = enrollmentData.discountReason.trim();
      }

      await api.post('/students/enroll', enrollmentPayload);
      // axios throws on non-2xx, so reaching here means success
      toast.success('Student enrolled successfully!');
      setShowEnrollSuccess(true);
      fetchStudents();
      enrollCloseTimer.current = setTimeout(() => {
        setShowEnrollSuccess(false);
        setEnrollmentData({ courseId: '', batchId: '', paymentPlan: 'full', numInstallments: 2, installmentAmounts: [], amountPaid: '', customTotalAmount: '', discountReason: '' });
        setAvailableBatches([]);
        setIsEnrollDialogOpen(false);
      }, 2500);
    } catch (error) {
      console.error('Error enrolling student:', error);
      // Keep dialog open on failure
      toast.error(getApiErrorMessage(error, 'Failed to enroll student'));
    } finally {
      setLoading(false);
    }
  };

  const handleTransferSubmit = async (e) => {
    e.preventDefault();

    if (!selectedStudent || !transferData.toCourseId || !transferData.toBatchId || !transferData.transferReason) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setTransferLoading(true);

      const transferPayload = {
        studentId:         selectedStudent._id,
        fromBatchId:       transferData.fromBatchId || undefined,
        toCourseId:        transferData.toCourseId,
        toBatchId:         transferData.toBatchId,
        transferReason:    transferData.transferReason,
        feeAdjustmentType: transferData.feeAdjustmentType,
        numInstallments:   Number(transferData.numInstallments),
        notes:             transferData.notes
      };

      const response = await api.post('/student-transfer/transfer', transferPayload);

      if (response.data.success) {
        setTransferData({
          fromBatchId:       '',
          toCourseId:        '',
          toBatchId:         '',
          transferReason:    '',
          feeAdjustmentType: 'transfer_fee',
          numInstallments:   1,
          notes:             ''
        });
        setTransferPreview(null);
        setTransferAvailableBatches([]);
        setIsTransferDialogOpen(false);
        toast.success('Student transferred successfully!');
        fetchStudents(); // Refresh the list
      }
    } catch (error) {
      console.error('Error transferring student:', error);
      toast.error(getApiErrorMessage(error, 'Failed to transfer student'));
    } finally {
      setTransferLoading(false);
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'emi': return 'bg-orange-100 text-orange-800';
      case 'pending': return 'bg-red-100 text-red-800';
      case 'active': return 'bg-green-100 text-green-800';
      case 'inactive': return 'bg-gray-100 text-gray-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getDocumentIcon = (type) => {
    switch (type) {
      case 'photo':
        return <PhotoIcon className="h-4 w-4" />;
      default:
        return <DocumentIcon className="h-4 w-4" />;
    }
  };

  const selectedCourse = courses.find(c => c._id === enrollmentData.courseId);
  const selectedBatch = availableBatches.find(b => b._id === enrollmentData.batchId);

  // Fee for enrollment: pick online/offline based on selected batch mode
  const applicableFee = selectedCourse
    ? (selectedBatch?.mode === 'offline'
        ? (selectedCourse.offlineFee ?? selectedCourse.onlineFee ?? 0)
        : (selectedCourse.onlineFee ?? selectedCourse.offlineFee ?? 0))
    : 0;

  const transferSelectedCourse = courses.find(c => c._id === transferData.toCourseId);
  const transferSelectedBatch = transferData.toBatchId ?
    (() => {
      // Check in same course batches
      if (transferAvailableBatches?.sameCourseBatches) {
        const found = transferAvailableBatches.sameCourseBatches.find(b => b._id === transferData.toBatchId);
        if (found) return found;
      }
      // Check in other course batches
      if (transferAvailableBatches?.otherCourseBatches) {
        const found = transferAvailableBatches.otherCourseBatches.find(b => b._id === transferData.toBatchId);
        if (found) return found;
      }
      return null;
    })() : null;

  // Fee for transfer: pick online/offline based on destination batch mode
  const transferApplicableFee = transferSelectedCourse
    ? (transferSelectedBatch?.mode === 'offline'
        ? (transferSelectedCourse.offlineFee ?? transferSelectedCourse.onlineFee ?? 0)
        : (transferSelectedCourse.onlineFee ?? transferSelectedCourse.offlineFee ?? 0))
    : 0;

  // Students come pre-filtered from the backend; use directly
  const filteredStudents = studentsList;

  // Stats from backend (global, unaffected by current filter)
  const totalStudents       = globalStats.totalStudents     || 0;
  const activeStudents      = globalStats.activeStudents    || 0;
  const discontinuedStudents = globalStats.discontinuedCount || 0;
  const totalRevenue        = globalStats.totalRevenue      || 0;
  const pendingAmount       = globalStats.totalPendingAmount || 0;

  const handleApplyFilters = () => {
    setApplied({ ...draft });
    setPage(1);
    setFilterOpen(false);
  };

  const handleClearFilters = () => {
    setDraft(EMPTY_DRAFT);
    setApplied(EMPTY_DRAFT);
    setPage(1);
    setFilterOpen(false);
  };

  const handleExportStudents = () => {
    const headers = [
      'Name', 'Email', 'Phone',
      'Active Course(s)', 'Transferred Course(s)', 'Discontinued Course(s)',
      'Total Paid (₹)', 'Pending (₹)',
      'Fee Status', 'Enrollment Status', 'Join Date',
    ];
    const rows = filteredStudents.map((s) => {
      const all          = s.enrolledCourses || [];
      const active       = all.filter(c => !c.assignmentStatus || c.assignmentStatus === 'active');
      const transferred  = all.filter(c => c.assignmentStatus === 'transferred');
      const discontinued = all.filter(c => c.assignmentStatus === 'discontinued');
      const feeStatus    = active.length > 0 ? (active[0].paymentStatus || 'pending') : (all[0]?.paymentStatus || '-');
      const enrollStatus = all.every(c => c.assignmentStatus === 'discontinued') ? 'Discontinued'
                         : all.some(c => c.assignmentStatus === 'transferred') ? 'Transferred'
                         : 'Active';
      return [
        s.name  || '-',
        s.email || '-',
        s.phone || '-',
        active.map(c => c.courseName).join('; ') || '-',
        transferred.map(c => c.courseName).join('; ') || '-',
        discontinued.map(c => c.courseName).join('; ') || '-',
        s.totalAmountPaid || 0,
        s.pendingAmount   || 0,
        feeStatus,
        enrollStatus,
        s.joinDate ? formatDateForCsv(s.joinDate) : '-',
      ];
    });
    downloadCsv(headers, rows, `students-export-${new Date().toISOString().slice(0, 10)}.csv`);
    toast.success(`Exported ${rows.length} student records`);
  };

  return (
    <>
      <AnimationStyles />
      <div className="space-y-6 text-sm font-sans leading-relaxed p-6 bg-gradient-to-br from-gray-50/80 to-violet-50/30 min-h-screen">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Student Management</h1>
            <p className="text-gray-600">Manage students and their course enrollments</p>
          </div>

          <div className="flex items-center gap-2">
            {/* Filter popup button */}
            {(() => {
              const activeCount = [
                applied.search,
                applied.course !== 'all',
                applied.status !== 'all',
                applied.fromDate,
                applied.toDate,
              ].filter(Boolean).length;
              return (
                <button
                  onClick={() => setFilterOpen(true)}
                  className="relative flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 hover:border-blue-300 transition-all text-sm font-medium text-gray-700 shadow-sm"
                >
                  <FunnelIcon className="h-4 w-4 text-blue-500" />
                  Search &amp; Filters
                  {activeCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-blue-600 text-white text-[10px] font-bold flex items-center justify-center">
                      {activeCount}
                    </span>
                  )}
                </button>
              );
            })()}
            <Button
              data-testid="add-student-button"
              onClick={() => setIsAddDialogOpen(true)}
              className="bg-gradient-to-r from-blue-500 to-violet-500 hover:from-blue-600 hover:to-violet-600 text-white"
              disabled={loading}
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add New Student
            </Button>
          </div>
        </div>

        {/* Stats Cards with Loading State */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {loading ? (
            <>
              <StatsCardSkeleton color="blue" />
              <StatsCardSkeleton color="green" />
              <StatsCardSkeleton color="red" />
              <StatsCardSkeleton color="violet" />
              <StatsCardSkeleton color="orange" />
            </>
          ) : (
            <>
              <Card
                className={`card-glass border-white/50 cursor-pointer transition-all hover:shadow-md ${applied.status === 'all' ? 'ring-2 ring-blue-400' : ''}`}
                onClick={() => { setApplied(a => ({ ...a, status: 'all' })); setDraft(d => ({ ...d, status: 'all' })); setPage(1); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 font-data">Total Students</p>
                      <p className="text-2xl font-bold text-gray-900">{totalStudents}</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <UserIcon className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`card-glass border-white/50 cursor-pointer transition-all hover:shadow-md ${applied.status === 'active' ? 'ring-2 ring-green-400' : ''}`}
                onClick={() => { setApplied(a => ({ ...a, status: 'active' })); setDraft(d => ({ ...d, status: 'active' })); setPage(1); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 font-data">Active</p>
                      <p className="text-2xl font-bold text-gray-900">{activeStudents}</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg">
                      <CheckCircleIcon className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card
                className={`card-glass border-white/50 cursor-pointer transition-all hover:shadow-md ${applied.status === 'discontinued' ? 'ring-2 ring-red-400' : ''}`}
                onClick={() => { setApplied(a => ({ ...a, status: 'discontinued' })); setDraft(d => ({ ...d, status: 'discontinued' })); setPage(1); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600 font-data">Discontinued</p>
                      <p className="text-2xl font-bold text-red-600">{discontinuedStudents}</p>
                    </div>
                    <div className="p-2 bg-red-100 rounded-lg">
                      <NoSymbolIcon className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              {!isCounselor && (
                <Card className="card-glass border-white/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 font-data">Total Revenue</p>
                        <p className="text-2xl font-bold text-gray-900">
                          ₹{totalRevenue.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-2 bg-violet-100 rounded-lg">
                        <CurrencyRupeeIcon className="h-6 w-6 text-violet-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {!isCounselor && (
                <Card className="card-glass border-white/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600 font-data">Pending Amount</p>
                        <p className="text-2xl font-bold text-gray-900">
                          ₹{pendingAmount.toLocaleString()}
                        </p>
                      </div>
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <ClockIcon className="h-6 w-6 text-orange-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>

        {/* ── Filter Popup Modal ── */}
        {filterOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setFilterOpen(false)} />

            {/* Panel */}
            <div className="relative z-10 w-full max-w-xl bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-gradient-to-r from-blue-600 to-violet-600">
                <div className="flex items-center gap-2">
                  <FunnelIcon className="h-5 w-5 text-white" />
                  <span className="text-base font-semibold text-white">Search &amp; Filters</span>
                </div>
                <button onClick={() => setFilterOpen(false)} className="text-white/80 hover:text-white transition-colors">
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {/* Body */}
              <div className="p-5 space-y-4">
                {/* Search */}
                <div className="flex flex-col gap-1">
                  <Label className="text-xs font-medium text-gray-600">Search</Label>
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by name, email, or phone…"
                      value={draft.search}
                      onChange={(e) => setDraft(d => ({ ...d, search: e.target.value }))}
                      className="pl-9 h-9 text-sm"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Course */}
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs font-medium text-gray-600">Course</Label>
                    <Select value={draft.course} onValueChange={(v) => setDraft(d => ({ ...d, course: v }))}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="All Courses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Courses</SelectItem>
                        {courses.map((course) => (
                          <SelectItem key={course._id} value={course._id}>{course.courseName}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Status */}
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs font-medium text-gray-600">Status</Label>
                    <Select value={draft.status} onValueChange={(v) => setDraft(d => ({ ...d, status: v }))}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Students</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="discontinued">Discontinued</SelectItem>
                        <SelectItem value="transferred">Has Transfer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* From Date */}
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs font-medium text-gray-600">Enrolled From</Label>
                    <Input
                      type="date"
                      value={draft.fromDate}
                      onChange={(e) => setDraft(d => ({ ...d, fromDate: e.target.value }))}
                      className="h-9 text-sm"
                    />
                  </div>

                  {/* To Date */}
                  <div className="flex flex-col gap-1">
                    <Label className="text-xs font-medium text-gray-600">Enrolled To</Label>
                    <Input
                      type="date"
                      value={draft.toDate}
                      onChange={(e) => setDraft(d => ({ ...d, toDate: e.target.value }))}
                      className="h-9 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 bg-gray-50">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClearFilters}
                  className="text-sm gap-1.5"
                >
                  ↺ Clear All
                </Button>
                <Button
                  size="sm"
                  onClick={handleApplyFilters}
                  className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-5"
                >
                  Apply Filters
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Students Table with Loading State */}
        <Card className="card-glass border-white/50 overflow-hidden">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>
                  {statusFilter === 'discontinued' ? 'Discontinued Students' :
                   statusFilter === 'active' ? 'Active Students' :
                   statusFilter === 'transferred' ? 'Students with Transfers' :
                   'All Students'}
                </CardTitle>
                <CardDescription className="font-data">
                  {statusFilter === 'discontinued'
                    ? 'Students who have dropped from their courses'
                    : 'Manage student information and enrollments'}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {applied.status !== 'all' && (
                  <Button variant="ghost" size="sm" onClick={() => { setApplied(a => ({ ...a, status: 'all' })); setDraft(d => ({ ...d, status: 'all' })); setPage(1); }} className="text-xs text-gray-500">
                    ✕ Clear filter
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportStudents}
                  disabled={filteredStudents.length === 0}
                  className="text-xs gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50"
                >
                  ↓ Export CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <TableSkeleton />
            ) : filteredStudents.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No students found for the selected filters.
              </div>
            ) : (
              <div className="overflow-x-auto p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#6F00FF] hover:bg-[#6F00FF]">
                      <TableHead className="text-white font-semibold font-data text-[13px] w-8">#</TableHead>
                      <TableHead className="text-white font-semibold font-data text-[13px]">Student</TableHead>
                      <TableHead className="text-white font-semibold font-data text-[13px]">Enrolled Courses</TableHead>
                      <TableHead className="text-white font-semibold font-data text-[13px]">Course Fee</TableHead>
                      <TableHead className="text-white font-semibold font-data text-[13px]">Total Paid</TableHead>
                      <TableHead className="text-white font-semibold font-data text-[13px]">Pending</TableHead>
                      <TableHead className="text-white font-semibold font-data text-[13px]">Status</TableHead>
                      <TableHead className="text-center text-white font-semibold font-data text-[13px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStudents.map((student, index) => {
                      const isFullyDiscontinued = (student.enrolledCourses || []).length > 0 &&
                        (student.enrolledCourses || []).every(c => c.assignmentStatus === 'discontinued');
                      // Sum totalAmount across all active enrollments (backend field is `totalAmount`)
                      const totalCourseFee = (student.enrolledCourses || [])
                        .filter(c => c.assignmentStatus === 'active')
                        .reduce((sum, c) => sum + (c.totalAmount || 0), 0);
                      const serialNo = (page - 1) * PAGE_SIZE + index + 1;
                      return (
                      <TableRow key={student._id} className={`transition-colors ${isFullyDiscontinued ? 'bg-red-50/40 hover:bg-red-50/60' : 'hover:bg-violet-50/50'}`}>
                        <TableCell className="py-3 text-xs text-gray-400 font-medium text-center">
                          {serialNo}
                        </TableCell>
                        <TableCell className="py-3 font-data">
                          <div>
                            <div className="flex items-center gap-1.5">
                              <p className={`font-medium text-sm ${isFullyDiscontinued ? 'text-red-500' : 'text-gray-900'}`}>{student.name}</p>
                              {isFullyDiscontinued && (
                                <Badge className="text-[9px] px-1.5 py-0 bg-red-100 text-red-600 border border-red-200">Discontinued</Badge>
                              )}
                            </div>
                            {student.studentId && (
                              <p className="text-xs font-mono font-semibold text-violet-600">{student.studentId}</p>
                            )}
                            <p className="text-xs text-gray-500">{student.email}</p>
                            <p className="text-xs text-gray-500">{student.phone}</p>
                          </div>
                        </TableCell>
                        <TableCell className="py-3">
                          <div className="space-y-1.5">
                            {(() => {
                              const allCourses = student.enrolledCourses || [];
                              const active = allCourses.filter(c => !c.assignmentStatus || c.assignmentStatus === 'active');
                              const transferred = allCourses.filter(c => c.assignmentStatus === 'transferred');
                              const discontinued = allCourses.filter(c => c.assignmentStatus === 'discontinued');
                              if (allCourses.length === 0) {
                                return <p className="text-xs text-gray-500">No courses</p>;
                              }
                              return (
                                <>
                                  {active.slice(0, 2).map((course, index) => (
                                    <div key={`a-${index}`} className="flex items-center gap-1 flex-wrap">
                                      <Badge variant="outline" className="text-xs px-2 py-1">
                                        {course.courseName}
                                      </Badge>
                                      <Badge className={`text-xs px-2 py-1 ${getPaymentStatusColor(course.paymentStatus)}`}>
                                        {course.paymentStatus || 'pending'}
                                      </Badge>
                                    </div>
                                  ))}
                                  {transferred.slice(0, 2).map((course, index) => (
                                    <div key={`t-${index}`} className="flex items-center gap-1 flex-wrap">
                                      <Badge variant="outline" className="text-xs px-2 py-1 text-gray-400 border-gray-300 line-through">
                                        {course.courseName}
                                      </Badge>
                                      <Badge className="text-xs px-2 py-1 bg-gray-100 text-gray-600 border border-gray-300">
                                        Transferred{course.transferredTo ? ` → ${course.transferredTo.courseName}` : ''}
                                      </Badge>
                                    </div>
                                  ))}
                                  {discontinued.map((course, index) => (
                                    <div key={`d-${index}`} className="space-y-0.5">
                                      <div className="flex items-center gap-1 flex-wrap">
                                        <Badge variant="outline" className="text-xs px-2 py-1 text-red-400 border-red-200 line-through">
                                          {course.courseName}
                                        </Badge>
                                        <Badge className="text-xs px-2 py-1 bg-red-50 text-red-600 border border-red-200">
                                          Discontinued
                                        </Badge>
                                      </div>
                                      {course.discontinuedAt && (
                                        <p className="text-[10px] text-red-400 pl-0.5">
                                          on {new Date(course.discontinuedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                                          {course.refundAmount > 0 && ` · Refund ₹${course.refundAmount.toLocaleString()}`}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                  {(active.length + transferred.length + discontinued.length) > 4 && (
                                    <p className="text-xs text-gray-500">+{(active.length + transferred.length + discontinued.length) - 4} more</p>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </TableCell>
                        <TableCell className="py-3">
                          <p className="font-medium text-blue-600 text-sm">
                            {totalCourseFee > 0 ? `₹${totalCourseFee.toLocaleString()}` : <span className="text-gray-400 text-xs">—</span>}
                          </p>
                        </TableCell>
                        <TableCell className="py-3">
                          <p className="font-medium text-green-600 text-sm">
                            ₹{(student.totalAmountPaid || 0).toLocaleString()}
                          </p>
                        </TableCell>
                        <TableCell className="py-3">
                          <p className="font-medium text-orange-600 text-sm">
                            ₹{(student.pendingAmount || 0).toLocaleString()}
                          </p>
                        </TableCell>
                        <TableCell className="py-3">
                          <Badge className={getPaymentStatusColor(student.status) + " text-xs"}>
                            {student.status ? student.status.charAt(0).toUpperCase() + student.status.slice(1) : 'Active'}
                          </Badge>
                        </TableCell>
                        <TableCell className="py-3 text-center align-middle">
                          <div className="flex flex-wrap items-center justify-center gap-2">
                            <Button
                              data-testid={`view-student-${student._id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleOpenHistory(student)}
                              className="h-8 w-8 p-0"
                              title="View History"
                            >
                              <EyeIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              data-testid={`enroll-student-${student._id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleEnrollStudent(student)}
                              className="h-8 w-8 p-0"
                              title="Enroll in Course"
                            >
                              <PlusIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              data-testid={`transfer-student-${student._id}`}
                              size="sm"
                              variant="outline"
                              onClick={() => handleTransferStudent(student)}
                              className="h-8 w-8 p-0 text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Transfer Course"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDiscontinueStudent(student)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Discontinue Student"
                            >
                              <XCircleIcon className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>

                {/* ── Pagination ──────────────────────────────────────────── */}
                {pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/60">
                    <p className="text-xs text-gray-500">
                      Showing <span className="font-semibold text-gray-700">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, pagination.totalStudents)}</span> of <span className="font-semibold text-gray-700">{pagination.totalStudents}</span> students
                    </p>
                    <div className="flex items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={page <= 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        className="h-7 px-2.5 text-xs"
                      >
                        ← Prev
                      </Button>
                      {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                        .filter(p => p === 1 || p === pagination.totalPages || Math.abs(p - page) <= 1)
                        .reduce((acc, p, idx, arr) => {
                          if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                          acc.push(p);
                          return acc;
                        }, [])
                        .map((p, i) =>
                          p === '...' ? (
                            <span key={`ellipsis-${i}`} className="px-1 text-xs text-gray-400">…</span>
                          ) : (
                            <Button
                              key={p}
                              size="sm"
                              variant={p === page ? 'default' : 'outline'}
                              onClick={() => setPage(p)}
                              className={`h-7 w-7 p-0 text-xs ${p === page ? 'bg-blue-600 hover:bg-blue-700 text-white' : ''}`}
                            >
                              {p}
                            </Button>
                          )
                        )}
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={page >= pagination.totalPages}
                        onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
                        className="h-7 px-2.5 text-xs"
                      >
                        Next →
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Student Dialog */}
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-2xl max-h-[92vh] flex flex-col">
            {/* Gradient header */}
            <div className="bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-5 flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-white/20 flex items-center justify-center">
                  <PlusCircleIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h2 className="text-base font-semibold text-white">Add New Student</h2>
                  <p className="text-xs text-blue-200 mt-0.5">Enter student details to create a profile</p>
                </div>
              </div>
            </div>
            <form onSubmit={handleAddStudent} className="flex flex-col flex-1 overflow-hidden">
              <div className="grid gap-4 py-4 px-6 overflow-y-auto flex-1">
                {/* Basic Information */}
                <div className="space-y-4">
                  <p className="text-[11px] font-semibold text-blue-600 uppercase tracking-widest">Basic Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        data-testid="student-name-input"
                        id="name"
                        value={newStudent.name}
                        onChange={(e) => setNewStudent({ ...newStudent, name: e.target.value })}
                        placeholder="Enter student name"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        data-testid="student-email-input"
                        id="email"
                        type="text"
                        value={newStudent.email}
                        onChange={(e) => setNewStudent({ ...newStudent, email: e.target.value })}
                        placeholder="student@email.com or email1/email2"
                        required
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        data-testid="student-phone-input"
                        id="phone"
                        type="text"
                        value={newStudent.phone}
                        onChange={(e) => setNewStudent({ ...newStudent, phone: e.target.value })}
                        placeholder="9876543210 or 9876543210/8765432109"
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="admissionSource">Admission Source</Label>
                      <Select
                        value={newStudent.admissionSource}
                        onValueChange={(value) => setNewStudent({ ...newStudent, admissionSource: value })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="website">Website</SelectItem>
                          <SelectItem value="walkin">Walk-in</SelectItem>
                          <SelectItem value="reference">Reference</SelectItem>
                          <SelectItem value="phone_enquiry">Phone Enquiry</SelectItem>
                          <SelectItem value="social">Social Media</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                {/* Guardian Information */}
                {/* <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Guardian Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="guardianName">Guardian Name</Label>
                      <Input
                        id="guardianName"
                        value={newStudent.guardian.name}
                        onChange={(e) => setNewStudent({
                          ...newStudent,
                          guardian: { ...newStudent.guardian, name: e.target.value }
                        })}
                        placeholder="Guardian's full name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="guardianPhone">Guardian Phone</Label>
                      <Input
                        id="guardianPhone"
                        value={newStudent.guardian.phone}
                        onChange={(e) => setNewStudent({
                          ...newStudent,
                          guardian: { ...newStudent.guardian, phone: e.target.value }
                        })}
                        placeholder="+91 9876543210"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="guardianEmail">Guardian Email</Label>
                      <Input
                        id="guardianEmail"
                        type="email"
                        value={newStudent.guardian.email}
                        onChange={(e) => setNewStudent({
                          ...newStudent,
                          guardian: { ...newStudent.guardian, email: e.target.value }
                        })}
                        placeholder="guardian@email.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="relationship">Relationship</Label>
                      <Select
                        value={newStudent.guardian.relationship}
                        onValueChange={(value) => setNewStudent({
                          ...newStudent,
                          guardian: { ...newStudent.guardian, relationship: value }
                        })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select relationship" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="father">Father</SelectItem>
                          <SelectItem value="mother">Mother</SelectItem>
                          <SelectItem value="guardian">Guardian</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div> */}

                {/* Address Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Address</h3>
                  <div className="space-y-2">
                    <Label htmlFor="street">Street Address</Label>
                    <Input
                      id="street"
                      value={newStudent.address.street}
                      onChange={(e) => setNewStudent({
                        ...newStudent,
                        address: { ...newStudent.address, street: e.target.value }
                      })}
                      placeholder="House no., street name"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City</Label>
                      <Input
                        id="city"
                        value={newStudent.address.city}
                        onChange={(e) => setNewStudent({
                          ...newStudent,
                          address: { ...newStudent.address, city: e.target.value }
                        })}
                        placeholder="City"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">State</Label>
                      <Input
                        id="state"
                        value={newStudent.address.state}
                        onChange={(e) => setNewStudent({
                          ...newStudent,
                          address: { ...newStudent.address, state: e.target.value }
                        })}
                        placeholder="State"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="pincode">Pincode</Label>
                      <Input
                        id="pincode"
                        value={newStudent.address.pincode}
                        onChange={(e) => setNewStudent({
                          ...newStudent,
                          address: { ...newStudent.address, pincode: e.target.value }
                        })}
                        placeholder="Pincode"
                      />
                    </div>
                  </div>
                </div>

                {/* Documents Section with Upload Loading */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm">Documents</h3>
                  <div className="space-y-3">
                    {DOCUMENT_TYPES.map((docType) => {
                      const existingDoc = newStudent.documents.find(doc => doc.type === docType.value);
                      const isUploading = uploadingFiles[docType.value];

                      if (isUploading) {
                        return <FileUploadSkeleton key={docType.value} type={docType.value} />;
                      }

                      return (
                        <div key={docType.value} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center space-x-3">
                            {getDocumentIcon(docType.value)}
                            <div>
                              <Label className="text-sm font-medium">{docType.label}</Label>
                              {existingDoc && (
                                <p className="text-xs text-gray-500 truncate max-w-xs">
                                  {existingDoc.fileName}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            {existingDoc ? (
                              <>
                                <a
                                  href={existingDoc.fileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  View
                                </a>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleFileDelete(existingDoc)}
                                  disabled={isUploading || deletingDocType === existingDoc.type}
                                  className="h-8 w-8 p-0"
                                >
                                  {deletingDocType === existingDoc.type ? (
                                    <svg className="animate-spin h-4 w-4 text-red-500" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                                  ) : (
                                    <TrashIcon className="h-4 w-4 text-red-500" />
                                  )}
                                </Button>
                              </>
                            ) : (
                              <div className="relative">
                                <Input
                                  type="file"
                                  accept={docType.value === 'photo' ? 'image/*' : '*/*'}
                                  onChange={(e) => handleFileChange(e, docType.value)}
                                  className="hidden"
                                  id={`file-${docType.value}`}
                                  disabled={isUploading}
                                />
                                <Label
                                  htmlFor={`file-${docType.value}`}
                                  className={`cursor-pointer px-3 py-2 text-sm border rounded-md ${isUploading
                                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                    : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                    }`}
                                >
                                  {isUploading ? 'Uploading...' : 'Upload'}
                                </Label>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 flex-shrink-0">
                <Button type="button" variant="outline" size="sm" onClick={() => setIsAddDialogOpen(false)} className="text-sm">Cancel</Button>
                <Button data-testid="create-student-submit" type="submit" size="sm" disabled={loading}
                  className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white text-sm px-6 gap-2">
                  {loading
                    ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Adding…</>
                    : <><PlusCircleIcon className="h-3.5 w-3.5" /> Add Student</>}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* ── Enrollment Dialog ─────────────────────────────────────────────── */}
        <Dialog open={isEnrollDialogOpen} onOpenChange={v => {
          if (!v) {
            clearTimeout(enrollCloseTimer.current);
            setShowEnrollSuccess(false);
            setEnrollmentData({ courseId:'', batchId:'', paymentPlan:'full', numInstallments:2, installmentAmounts:[], amountPaid:'', customTotalAmount:'', discountReason:'' });
            setAvailableBatches([]);
          }
          setIsEnrollDialogOpen(v);
        }}>
          <DialogContent className="!top-0 !bottom-0 !left-20 !right-0 !translate-x-0 !translate-y-0 !max-w-none !w-[calc(100vw-80px)] !h-screen !rounded-none p-0 gap-0 flex flex-col overflow-y-auto">

            {/* Gradient header */}
            <div className="bg-gradient-to-r from-blue-600 to-violet-600 px-6 py-4 flex-shrink-0">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
                    <AcademicCapIcon className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-base font-semibold text-white leading-tight">Enroll in a Course</h2>
                    <p className="text-xs text-white/70 mt-0.5 truncate">{selectedStudent?.name}</p>
                  </div>
                </div>
                <Button size="sm" variant="secondary" type="button"
                  onClick={() => setIsEnrollDialogOpen(false)}
                  className="h-8 px-3 text-xs bg-white/20 hover:bg-white/30 text-white border-0 flex-shrink-0">
                  Close
                </Button>
              </div>
            </div>

            {/* ── SUCCESS SCREEN ── */}
            {showEnrollSuccess && (
              <div className="flex-1 flex flex-col items-center justify-center gap-6 px-8 py-16 text-center">
                <div className="h-24 w-24 rounded-full bg-blue-100 flex items-center justify-center shadow-lg">
                  <CheckCircleSolid className="h-14 w-14 text-blue-500" />
                </div>
                <div className="space-y-2">
                  <h3 className="text-2xl font-bold text-gray-900">Enrolled Successfully!</h3>
                  <p className="text-sm text-gray-500 max-w-xs">
                    {selectedStudent?.name} has been enrolled in the batch. This window will close automatically.
                  </p>
                </div>
                <div className="w-56 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-blue-500 rounded-full" style={{ animation: 'countdown 2.5s linear forwards' }} />
                </div>
                <p className="text-xs text-gray-400">Closing in 2 seconds…</p>
              </div>
            )}

            {!showEnrollSuccess && <form onSubmit={handleEnrollmentSubmit} className="flex flex-col">
              <div className="px-6 py-4 space-y-4">

                {/* Course Selection */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-gray-700">Select Course <span className="text-red-500">*</span></Label>
                  <CourseSearchSelect
                    courses={courses}
                    loading={coursesLoading}
                    value={enrollmentData.courseId}
                    onChange={handleCourseChange}
                    placeholder="Choose a course"
                    showFees
                    disabledIds={(selectedStudent?.enrolledCourses || []).map(c => String(c.courseId))}
                    disabledLabel="— Already Enrolled"
                  />
                </div>

                {/* Batch Cards */}
                {enrollmentData.courseId && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-gray-700">Select Batch <span className="text-red-500">*</span></Label>
                    {loadingBatches ? (
                      <div className="space-y-2">
                        {[1, 2].map(i => <div key={i} className="h-20 rounded-xl bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 animate-pulse" />)}
                      </div>
                    ) : availableBatches.length > 0 ? (
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {availableBatches.map((batch) => {
                          const isFull = isBatchFull(batch);
                          const seats  = batch.capacity - batch.assignedStudents;
                          const sel    = enrollmentData.batchId === batch._id;
                          const DAYS_SHORT = { Monday:'Mon', Tuesday:'Tue', Wednesday:'Wed', Thursday:'Thu', Friday:'Fri', Saturday:'Sat', Sunday:'Sun' };
                          const DAYS_ORD   = ['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'];
                          const fmt12 = (t) => { if (!t) return ''; const [h,m]=t.split(':').map(Number); return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`; };
                          const groups = [];
                          (batch.schedule || []).forEach(s => {
                            const last = groups[groups.length-1];
                            const same = last && last.st===s.startTime && last.et===s.endTime;
                            const con  = last && DAYS_ORD.indexOf(s.day)===DAYS_ORD.indexOf(last.ld)+1;
                            if (same && con) { last.ld=s.day; } else groups.push({ fd:s.day, ld:s.day, st:s.startTime, et:s.endTime });
                          });
                          const timing = groups.map(g => `${g.fd===g.ld?(DAYS_SHORT[g.fd]||g.fd):`${DAYS_SHORT[g.fd]||g.fd} – ${DAYS_SHORT[g.ld]||g.ld}`}${g.st&&g.et?` (${fmt12(g.st)} – ${fmt12(g.et)})`:''}` ).join(', ');
                          return (
                            <div key={batch._id}
                              onClick={() => !isFull && setEnrollmentData(p => ({ ...p, batchId: batch._id }))}
                              className={`p-3.5 border-2 rounded-xl cursor-pointer transition-all ${
                                sel  ? 'border-blue-500 bg-blue-50/60 shadow-sm' :
                                isFull ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60' :
                                         'border-gray-200 hover:border-blue-300 hover:bg-blue-50/40'
                              }`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap mb-1">
                                    <span className="font-semibold text-sm text-gray-900">{batch.name}</span>
                                    <Badge className={`text-[10px] px-1.5 py-0 capitalize ${batch.mode === 'online' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'}`}>{batch.mode}</Badge>
                                    {isFull
                                      ? <Badge className="text-[10px] px-1.5 py-0 bg-red-100 text-red-700">Full</Badge>
                                      : <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700">{seats} seat{seats !== 1 ? 's' : ''}</Badge>}
                                  </div>
                                  <div className="flex flex-wrap gap-3 text-[11px] text-gray-500">
                                    {timing && <span className="flex items-center gap-1"><ClockIcon className="h-3 w-3 text-blue-400"/>{timing}</span>}
                                    <span className="flex items-center gap-1"><UserIcon className="h-3 w-3"/>{batch.assignedStudents}/{batch.capacity}</span>
                                    <span>{new Date(batch.startDate).toLocaleDateString()} – {new Date(batch.endDate).toLocaleDateString()}</span>
                                  </div>
                                  <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                                    <div className={`h-1.5 rounded-full transition-all ${isFull ? 'bg-red-400' : sel ? 'bg-blue-500' : 'bg-gray-400'}`}
                                      style={{ width: `${Math.min(100,(batch.assignedStudents/batch.capacity)*100)}%` }} />
                                  </div>
                                </div>
                                {sel && <CheckCircleSolid className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="p-6 text-center border-2 border-dashed border-gray-200 rounded-xl">
                        <ExclamationTriangleIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
                        <p className="text-sm text-gray-500">No active batches for this course</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Payment Setup */}
                {enrollmentData.batchId && selectedCourse && selectedBatch && (() => {
                  const finalFee = enrollmentData.customTotalAmount ? Number(enrollmentData.customTotalAmount) : applicableFee;
                  const n        = enrollmentData.numInstallments;
                  const amounts  = enrollmentData.installmentAmounts;
                  const amtSum   = amounts.reduce((s, a) => s + (Number(a) || 0), 0);
                  const amtOk    = amounts.length === n && Math.abs(amtSum - finalFee) <= 1;
                  const discount = applicableFee - finalFee;
                  return (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 py-1">
                        <div className="h-px flex-1 bg-gray-100" />
                        <span className="text-[10px] font-semibold uppercase tracking-widest text-blue-600 px-2">Payment Setup</span>
                        <div className="h-px flex-1 bg-gray-100" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Final Fee <span className="text-gray-400">(optional)</span></Label>
                          <Input type="number" min="0" className="h-9 text-sm"
                            value={enrollmentData.customTotalAmount}
                            onChange={e => setEnrollmentData(p => ({ ...p, customTotalAmount: e.target.value, installmentAmounts: [] }))}
                            placeholder={`Default: ₹${applicableFee.toLocaleString()}`} />
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Adjustment Reason <span className="text-gray-400">(optional)</span></Label>
                          <Input className="h-9 text-sm"
                            value={enrollmentData.discountReason}
                            onChange={e => setEnrollmentData(p => ({ ...p, discountReason: e.target.value }))}
                            placeholder="e.g., scholarship" />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5">
                          <Label className="text-xs font-medium text-gray-700">Payment Plan</Label>
                          <Select value={enrollmentData.paymentPlan}
                            onValueChange={v => setEnrollmentData(p => ({ ...p, paymentPlan: v, installmentAmounts: [] }))}>
                            <SelectTrigger className="h-9 text-sm" data-testid="payment-plan-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="full">💳 Full Payment</SelectItem>
                              <SelectItem value="emi">📅 EMI / Installments</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {enrollmentData.paymentPlan === 'emi' && (
                          <div className="space-y-1.5">
                            <Label className="text-xs font-medium text-gray-700">Number of Installments</Label>
                            <Select value={String(n)}
                              onValueChange={v => {
                                const newN = Number(v);
                                const eq   = Math.floor(finalFee / newN);
                                const last = Math.round(finalFee - eq*(newN-1));
                                setEnrollmentData(p => ({ ...p, numInstallments: newN, installmentAmounts: Array.from({length:newN},(_,i)=>i===newN-1?last:eq) }));
                              }}>
                              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {[2,3,4].map(i => <SelectItem key={i} value={String(i)}>{i} installments</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      {enrollmentData.paymentPlan === 'emi' && (() => {
                        // Compute due dates: Inst i → startDate + (i) months
                        // Last installment capped to 1 month before batch end date
                        const batchStart = selectedBatch?.startDate ? new Date(selectedBatch.startDate) : null;
                        const batchEnd   = selectedBatch?.endDate   ? new Date(selectedBatch.endDate)   : null;
                        const fmtDue = (d) => d.toLocaleDateString('en-IN', { day:'2-digit', month:'short', year:'numeric' });
                        const dueDates = Array.from({ length: n }, (_, i) => {
                          if (!batchStart) return null;
                          const d = new Date(batchStart);
                          d.setMonth(d.getMonth() + i);
                          // Clamp last installment
                          if (i === n - 1 && batchEnd && d >= batchEnd) {
                            const capped = new Date(batchEnd);
                            capped.setMonth(capped.getMonth() - 1);
                            return capped;
                          }
                          return d;
                        });
                        const lastDue    = dueDates[n - 1];
                        const lastIsLate = batchEnd && lastDue && lastDue >= batchEnd;
                        return (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs font-medium text-gray-600">Installment schedule</p>
                              <button type="button" className="text-[11px] text-blue-600 hover:underline"
                                onClick={() => { const eq=Math.floor(finalFee/n); const last=Math.round(finalFee-eq*(n-1)); setEnrollmentData(p=>({...p,installmentAmounts:Array.from({length:n},(_,i)=>i===n-1?last:eq)})); }}>
                                Reset to equal
                              </button>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                              {Array.from({length:n}).map((_,i)=>(
                                <div key={i} className="space-y-1 bg-gray-50 border border-gray-200 rounded-lg p-2">
                                  <p className="text-[10px] font-semibold text-gray-500 uppercase">Inst. {i+1}</p>
                                  <Input type="number" min="1" className="h-8 text-xs"
                                    value={amounts[i]??''}
                                    onChange={e=>{const u=[...amounts];u[i]=e.target.value;setEnrollmentData(p=>({...p,installmentAmounts:u}));}}
                                    placeholder={`₹${Math.floor(finalFee/n).toLocaleString()}`} />
                                  {dueDates[i] && (
                                    <p className="text-[10px] text-blue-600 font-medium flex items-center gap-0.5">
                                      📅 {fmtDue(dueDates[i])}
                                    </p>
                                  )}
                                </div>
                              ))}
                            </div>
                            {lastIsLate && (
                              <div className="flex items-center gap-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                                ⚠️ Last installment capped to 1 month before batch end date.
                              </div>
                            )}
                            <div className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${amtOk?'bg-emerald-50 text-emerald-700':'bg-amber-50 text-amber-700'}`}>
                              <span>Total: <strong>₹{amtSum.toLocaleString()}</strong></span>
                              <span>Required: <strong>₹{finalFee.toLocaleString()}</strong></span>
                              {amtOk?<span className="font-semibold">✓ Matches</span>:<span>Diff: ₹{Math.abs(finalFee-amtSum).toLocaleString()}</span>}
                            </div>
                          </div>
                        );
                      })()}
                      {/* Summary */}
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 space-y-1.5 text-sm">
                        <p className="text-[10px] font-bold text-blue-800 uppercase tracking-widest mb-2">Enrollment Summary</p>
                        {[
                          { label:'Student', value:selectedStudent?.name },
                          { label:'Course',  value:selectedCourse.courseName },
                          { label:'Batch',   value:selectedBatch.name },
                          { label:`Fee (${selectedBatch.mode})`, value:`₹${applicableFee.toLocaleString()}`, bold:true },
                          ...(discount>0?[{label:'Discount',value:`-₹${discount.toLocaleString()}`,cls:'text-emerald-700'}]:[]),
                          ...(discount>0?[{label:'Final Fee',value:`₹${finalFee.toLocaleString()}`,bold:true,cls:'text-blue-700'}]:[]),
                          { label:'Plan', value:enrollmentData.paymentPlan==='emi'?`EMI · ${n} installments`:'Full Payment' },
                        ].map(({label,value,bold,cls})=>(
                          <div key={label} className="flex justify-between">
                            <span className="text-gray-600">{label}:</span>
                            <span className={`${bold?'font-semibold':'font-medium'} ${cls||''}`}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Sticky footer */}
              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/80 sticky bottom-0">
                <Button type="button" variant="outline" size="sm" className="h-9 text-sm"
                  onClick={() => { setIsEnrollDialogOpen(false); setEnrollmentData({courseId:'',batchId:'',paymentPlan:'full',numInstallments:2,installmentAmounts:[],amountPaid:'',customTotalAmount:'',discountReason:''}); setAvailableBatches([]); }}>
                  Cancel
                </Button>
                <Button data-testid="enroll-student-submit" type="submit" size="sm"
                  disabled={!enrollmentData.courseId || !enrollmentData.batchId || loading}
                  className="h-9 px-6 bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white gap-2">
                  {loading
                    ? <><svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg> Enrolling…</>
                    : <><AcademicCapIcon className="h-3.5 w-3.5"/> Enroll Student</>}
                </Button>
              </div>
            </form>}
          </DialogContent>
        </Dialog>

        {/* Transfer Dialog */}
        <Dialog open={isTransferDialogOpen} onOpenChange={setIsTransferDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex-col overflow-y-auto overflow-x-hidden">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ArrowPathIcon className="h-5 w-5 text-amber-500" />
                Transfer Student Course
              </DialogTitle>
              <DialogDescription>
                {selectedStudent && `Transfer ${selectedStudent.name} to a different course or batch`}
              </DialogDescription>
            </DialogHeader>

            {/* ── Enrollment & Payment History ─────────────────────────────── */}
            {transferHistoryLoading ? (
              <div className="mb-4 p-4 bg-gray-50 border border-gray-200 rounded-lg flex items-center gap-3">
                <svg className="animate-spin h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                <span className="text-sm text-gray-500">Loading enrollment history…</span>
              </div>
            ) : transferHistory?.enrollmentHistory?.length > 0 ? (
              <div className="mb-4">
                {/* Section header */}
                <div className="flex items-center gap-2 mb-3">
                  <ClockIcon className="h-4 w-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-700">Enrollment &amp; Payment History</span>
                  {transferHistory.totalTransfers > 0 && (
                    <Badge className="bg-orange-100 text-orange-700 text-xs">
                      {transferHistory.totalTransfers} Transfer{transferHistory.totalTransfers > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>

                <div className="space-y-3 max-h-80 overflow-y-auto pr-1">
                  {transferHistory.enrollmentHistory.map((enrollment, idx) => {
                    const isActive = enrollment.assignmentStatus === 'active';
                    const inv      = enrollment.invoice;
                    const credit   = inv?.transferMetadata?.creditAmount;
                    const paidPct  = inv && inv.totalAmount > 0
                      ? Math.round((inv.paidAmount / inv.totalAmount) * 100)
                      : 0;

                    return (
                      <div
                        key={enrollment._id}
                        className={`border-2 rounded-lg p-3 text-xs ${
                          isActive
                            ? 'border-green-400 bg-green-50'
                            : 'border-gray-200 bg-gray-50'
                        }`}
                      >
                        {/* Row 1: Course / Batch / Status */}
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-semibold text-sm text-gray-900">
                                {enrollment.batch?.course?.name || '—'}
                              </span>
                              <Badge className={`text-xs px-1.5 py-0 ${isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>
                                {isActive ? 'Active' : 'Transferred'}
                              </Badge>
                              {enrollment.batch?.mode && (
                                <Badge variant="outline" className="text-xs px-1.5 py-0 capitalize">
                                  {enrollment.batch.mode}
                                </Badge>
                              )}
                            </div>
                            <div className="text-gray-500 mt-0.5">
                              Batch: <span className="font-medium text-gray-700">{enrollment.batch?.name}</span>
                              {enrollment.batch?.code && <span className="ml-1 text-gray-400">({enrollment.batch.code})</span>}
                            </div>
                          </div>
                          <div className="text-right shrink-0 text-gray-400 leading-4">
                            <div>Enrolled: {new Date(enrollment.joinDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</div>
                            {enrollment.transferredAt && (
                              <div className="text-orange-500 mt-0.5">
                                Transferred: {new Date(enrollment.transferredAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Row 2: Fee Breakdown */}
                        {inv ? (
                          <>
                            <div className="grid grid-cols-3 gap-2 mb-2">
                              <div className="bg-white rounded p-1.5 text-center border border-gray-200">
                                <div className="text-gray-400">Course Fee</div>
                                <div className="font-semibold text-gray-800">₹{inv.totalAmount.toLocaleString('en-IN')}</div>
                              </div>
                              <div className="bg-white rounded p-1.5 text-center border border-gray-200">
                                <div className="text-gray-400">Paid</div>
                                <div className="font-semibold text-green-700">₹{inv.paidAmount.toLocaleString('en-IN')}</div>
                              </div>
                              <div className="bg-white rounded p-1.5 text-center border border-gray-200">
                                <div className="text-gray-400">{isActive ? 'Pending' : 'Was Pending'}</div>
                                <div className={`font-semibold ${inv.pendingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                  ₹{inv.pendingAmount.toLocaleString('en-IN')}
                                </div>
                              </div>
                            </div>

                            {/* Payment progress bar */}
                            <div className="mb-2">
                              <div className="flex justify-between text-gray-400 mb-0.5">
                                <span>Payment progress</span>
                                <span>{paidPct}%</span>
                              </div>
                              <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full transition-all bg-green-500"
                                  style={{ width: `${paidPct}%` }}
                                />
                              </div>
                            </div>

                            {/* Installment status dots */}
                            {inv.installments?.length > 1 && (
                              <div className="flex gap-1 mb-2">
                                {inv.installments.map((inst) => (
                                  <div
                                    key={inst.installmentNo}
                                    title={`Installment ${inst.installmentNo}: ₹${inst.amount.toLocaleString('en-IN')} — ${inst.status} (paid ₹${inst.paidAmount.toLocaleString('en-IN')})`}
                                    className={`flex-1 h-2 rounded-full cursor-help ${
                                      inst.status === 'paid'            ? 'bg-green-500'
                                      : inst.status === 'partially_paid' ? 'bg-yellow-400'
                                      : inst.status === 'overdue'        ? 'bg-red-500'
                                      : 'bg-gray-300'
                                    }`}
                                  />
                                ))}
                              </div>
                            )}

                            {/* Installment details table */}
                            {inv.installments?.length > 0 && (
                              <div className="border border-gray-200 rounded overflow-hidden mb-2">
                                <table className="w-full text-xs">
                                  <thead className="bg-gray-100">
                                    <tr>
                                      <th className="py-1 px-2 text-left font-medium text-gray-500">Installment</th>
                                      <th className="py-1 px-2 text-right font-medium text-gray-500">Amount</th>
                                      <th className="py-1 px-2 text-right font-medium text-gray-500">Paid</th>
                                      <th className="py-1 px-2 text-right font-medium text-gray-500">Remaining</th>
                                      <th className="py-1 px-2 text-center font-medium text-gray-500">Status</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-100">
                                    {inv.installments.map((inst) => (
                                      <tr key={inst.installmentNo} className="bg-white">
                                        <td className="py-1 px-2 text-gray-600">#{inst.installmentNo} · {new Date(inst.dueDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}</td>
                                        <td className="py-1 px-2 text-right">₹{inst.amount.toLocaleString('en-IN')}</td>
                                        <td className="py-1 px-2 text-right text-green-700">₹{inst.paidAmount.toLocaleString('en-IN')}</td>
                                        <td className="py-1 px-2 text-right text-red-600">₹{inst.remainingAmount.toLocaleString('en-IN')}</td>
                                        <td className="py-1 px-2 text-center">
                                          <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${
                                            inst.status === 'paid'            ? 'bg-green-100 text-green-700'
                                            : inst.status === 'partially_paid' ? 'bg-yellow-100 text-yellow-700'
                                            : inst.status === 'overdue'        ? 'bg-red-100 text-red-700'
                                            : 'bg-gray-100 text-gray-600'
                                          }`}>
                                            {inst.status.replace('_', ' ')}
                                          </span>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}

                            {inv.discountAmount > 0 && (
                              <div className="text-gray-500 italic">
                                Discount applied: ₹{inv.discountAmount.toLocaleString('en-IN')}
                                {inv.discountReason && <span> — {inv.discountReason}</span>}
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-gray-400 italic">No fee invoice found for this enrollment.</div>
                        )}

                        {/* Transfer info footer */}
                        {!isActive && (enrollment.transferReason || (credit !== undefined && credit !== null)) && (
                          <div className="mt-2 pt-2 border-t border-gray-200 space-y-1">
                            {enrollment.transferReason && (
                              <div className="flex items-start gap-1 text-orange-700">
                                <ArrowPathIcon className="h-3 w-3 mt-0.5 shrink-0" />
                                <span><span className="font-medium">Transfer reason:</span> {enrollment.transferReason}</span>
                              </div>
                            )}
                            {credit !== undefined && credit !== null && (
                              <div className="flex items-center gap-1 text-blue-700">
                                <CurrencyRupeeIcon className="h-3 w-3 shrink-0" />
                                <span>
                                  <span className="font-semibold">₹{Number(credit).toLocaleString('en-IN')}</span>
                                  {' '}credited to next enrollment
                                  {inv?.transferMetadata?.refundAmount > 0 && (
                                    <span className="ml-2 text-green-700">
                                      · ₹{Number(inv.transferMetadata.refundAmount).toLocaleString('en-IN')} refunded
                                    </span>
                                  )}
                                </span>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Live preview hint on active enrollment */}
                        {isActive && transferPreview && (
                          <div className="mt-2 pt-2 border-t border-green-200">
                            <div className="flex items-center gap-1 text-blue-700">
                              <InformationCircleIcon className="h-3 w-3 shrink-0" />
                              <span>
                                After transfer:{' '}
                                <span className="font-semibold">
                                  ₹{(transferPreview.summary?.netAmountToPay ?? 0).toLocaleString('en-IN')}
                                </span>{' '}
                                due on new course
                                {(transferPreview.summary?.creditApplied ?? 0) > 0 && (
                                  <span className="ml-1 text-green-700">
                                    (₹{Number(transferPreview.summary.creditApplied).toLocaleString('en-IN')} credit applied)
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : null}

            <form onSubmit={handleTransferSubmit}>
              <div className="grid gap-5 py-4">

                {/* Destination Selection */}
                <div className="space-y-4">
                  <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                    <DocumentDuplicateIcon className="h-4 w-4 text-amber-500" />
                    Destination Course
                  </h4>

                  {/* Course Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="toCourse">Select Destination Course *</Label>
                    <Select
                      value={transferData.toCourseId}
                      onValueChange={handleTransferCourseChange}
                    >
                      <SelectTrigger data-testid="transfer-course-select">
                        <SelectValue placeholder="Choose a course" />
                      </SelectTrigger>
                      <SelectContent>
                        {courses.map((course) => (
                          <SelectItem key={course._id} value={course._id}>
                            {course.courseName}
                            {' · '}Online: ₹{(course.onlineFee ?? 0).toLocaleString()}
                            {' / '}Offline: ₹{(course.offlineFee ?? 0).toLocaleString()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Batch Selection */}
                  {transferData.toCourseId && (
                    <div className="space-y-3">
                      <Label>Select Destination Batch *</Label>
                      {loadingBatches ? (
                        <BatchSkeletonLoader />
                      ) : (transferAvailableBatches?.sameCourseBatches?.length > 0 || transferAvailableBatches?.otherCourseBatches?.length > 0) ? (
                        <div className="space-y-4 max-h-80 overflow-y-auto">
                          {/* Same Course Batches */}
                          {transferAvailableBatches.sameCourseBatches?.length > 0 && (
                            <div>
                              <p className="text-xs font-medium text-gray-500 mb-2">Same Course Batches</p>
                              {transferAvailableBatches.sameCourseBatches.map((batch) => {
                                const isFull = isBatchFull(batch);
                                const availableSeats = batch.capacity - batch.assignedStudents;

                                return (
                                  <div
                                    key={batch._id}
                                    onClick={() => {
                                      if (!isFull) {
                                        setTransferData({ ...transferData, toBatchId: batch._id });
                                      }
                                    }}
                                    className={`
                    p-4 border-2 rounded-lg cursor-pointer transition-all mb-2
                    ${transferData.toBatchId === batch._id
                                        ? 'border-amber-500 bg-amber-50'
                                        : isFull
                                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                          : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                                      }
                  `}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <h4 className="font-semibold text-base">{batch.name}</h4>
                                          <Badge variant="outline" className="capitalize">
                                            {batch.mode}
                                          </Badge>
                                          {isFull ? (
                                            <Badge className="bg-red-100 text-red-800">
                                              <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
                                              Full
                                            </Badge>
                                          ) : (
                                            <Badge className="bg-green-100 text-green-800">
                                              {availableSeats} seat{availableSeats !== 1 ? 's' : ''} left
                                            </Badge>
                                          )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                                          <div className="flex items-center gap-1">
                                            <CalendarIcon className="h-4 w-4" />
                                            <span>Starts: {new Date(batch.startDate).toLocaleDateString()}</span>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <UserIcon className="h-4 w-4" />
                                            <span>{batch.assignedStudents}/{batch.capacity} enrolled</span>
                                          </div>
                                        </div>
                                      </div>

                                      {transferData.toBatchId === batch._id && (
                                        <CheckCircleSolid className="h-6 w-6 text-amber-600 flex-shrink-0 ml-2" />
                                      )}
                                    </div>

                                    {/* Capacity Bar */}
                                    <div className="mt-2">
                                      <div className="w-full bg-gray-200 rounded-full h-1.5">
                                        <div
                                          className={`h-1.5 rounded-full transition-all ${isFull ? 'bg-red-500' : 'bg-green-500'}`}
                                          style={{ width: `${(batch.assignedStudents / batch.capacity) * 100}%` }}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Other Course Batches */}
                          {transferAvailableBatches.otherCourseBatches?.length > 0 && (
                            <div className="mt-4">
                              <p className="text-xs font-medium text-gray-500 mb-2">Other Courses</p>
                              {transferAvailableBatches.otherCourseBatches.map((batch) => {
                                const isFull = isBatchFull(batch);
                                const availableSeats = batch.capacity - batch.assignedStudents;

                                return (
                                  <div
                                    key={batch._id}
                                    onClick={() => {
                                      if (!isFull) {
                                        setTransferData({ ...transferData, toBatchId: batch._id });
                                      }
                                    }}
                                    className={`
                    p-4 border-2 rounded-lg cursor-pointer transition-all mb-2
                    ${transferData.toBatchId === batch._id
                                        ? 'border-amber-500 bg-amber-50'
                                        : isFull
                                          ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                                          : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                                      }
                  `}
                                  >
                                    <div className="flex items-start justify-between">
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2 mb-2">
                                          <h4 className="font-semibold text-base">{batch.name}</h4>
                                          <Badge variant="outline" className="capitalize">
                                            {batch.mode}
                                          </Badge>
                                          <Badge className="bg-blue-100 text-blue-800">
                                            {batch.courseId?.name || 'Course'}
                                          </Badge>
                                        </div>
                                        <div className="text-sm text-gray-600 space-y-0.5">
                                          <span className={`font-medium block ${batch.mode === 'online' ? 'text-blue-700' : 'text-gray-500'}`}>
                                            Online: ₹{(batch.courseId?.fees?.online?.totalAmount ?? 0).toLocaleString()}
                                          </span>
                                          <span className={`font-medium block ${batch.mode === 'offline' ? 'text-blue-700' : 'text-gray-500'}`}>
                                            Offline: ₹{(batch.courseId?.fees?.offline?.totalAmount ?? 0).toLocaleString()}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="p-6 text-center border-2 border-dashed border-gray-300 rounded-lg">
                          <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                          <p className="text-gray-600">No active batches available for this course</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Fee Adjustment + Installments */}
                {transferData.toBatchId && (
                  <div className="space-y-4">
                    <h4 className="text-sm font-semibold text-gray-700">Fee & Payment Options</h4>

                    {/* Adjustment type */}
                    <div className="space-y-2">
                      <Label>Adjustment Type</Label>
                      <Select
                        value={transferData.feeAdjustmentType}
                        onValueChange={(value) => {
                          setTransferData({ ...transferData, feeAdjustmentType: value });
                          setTransferPreview(null);
                        }}
                      >
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="transfer_fee">Credit Prior Payments + Pay Difference</SelectItem>
                          <SelectItem value="prorated_refund">Prorated Refund (based on time spent)</SelectItem>
                          <SelectItem value="full_refund">Full Refund (start fresh)</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        {transferData.feeAdjustmentType === 'transfer_fee' && 'All prior payments are credited to the new invoice.'}
                        {transferData.feeAdjustmentType === 'prorated_refund' && 'A portion of prior payments is refunded; the rest is credited.'}
                        {transferData.feeAdjustmentType === 'full_refund' && 'All prior payments are refunded in cash. New invoice starts from zero.'}
                      </p>
                    </div>

                    {/* Installment plan for new invoice */}
                    <div className="space-y-2">
                      <Label>Payment Plan for New Course <span className="text-gray-400 text-xs">(max 4 installments)</span></Label>
                      <div className="flex gap-2">
                        {[1, 2, 3, 4].map((n) => {
                          const fee = transferApplicableFee;
                          const perInst = fee > 0 ? Math.ceil(fee / n) : 0;
                          const isSelected = Number(transferData.numInstallments) === n;
                          return (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setTransferData({ ...transferData, numInstallments: n })}
                              className={`flex-1 border-2 rounded-lg p-2.5 text-center transition-all cursor-pointer ${
                                isSelected
                                  ? 'border-amber-500 bg-amber-50 text-amber-700'
                                  : 'border-gray-200 hover:border-amber-300 hover:bg-amber-50'
                              }`}
                            >
                              <p className="font-semibold text-sm">{n}</p>
                              <p className="text-xs text-gray-500">{n === 1 ? 'Full' : 'EMI'}</p>
                              {perInst > 0 && (
                                <p className="text-xs font-medium mt-0.5">≈₹{perInst.toLocaleString()}</p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Preview button */}
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={calculateTransferPreview}
                      disabled={!transferData.toCourseId || !transferData.toBatchId || previewLoading}
                    >
                      {previewLoading ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-600 mr-2" />Calculating...</>
                      ) : 'Preview Fee Breakdown'}
                    </Button>

                    {/* Preview result */}
                    {previewLoading ? (
                      <TransferPreviewSkeleton />
                    ) : transferPreview && (
                      <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg space-y-3">
                        <h5 className="font-semibold text-sm flex items-center gap-2">
                          <CurrencyRupeeIcon className="h-4 w-4 text-amber-600" />
                          Transfer Financial Summary
                        </h5>

                        {/* Old invoice */}
                        {transferPreview.currentInvoice && (
                          <div className="space-y-1 pb-2 border-b border-amber-200">
                            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Current Invoice</p>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Total Fee:</span>
                              <span className="font-medium">₹{transferPreview.currentInvoice.totalAmount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Already Paid:</span>
                              <span className="font-medium text-green-600">₹{transferPreview.currentInvoice.paidAmount.toLocaleString()}</span>
                            </div>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Pending:</span>
                              <span className="font-medium text-orange-600">₹{transferPreview.currentInvoice.pendingAmount.toLocaleString()}</span>
                            </div>
                          </div>
                        )}

                        {/* New course */}
                        <div className="space-y-1 pb-2 border-b border-amber-200">
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            New Course ({transferPreview.newCourse?.mode || transferSelectedBatch?.mode || 'offline'})
                          </p>
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">{transferPreview.newCourse.name} Fee:</span>
                            <span className="font-medium">₹{transferPreview.newCourse.totalAmount.toLocaleString()}</span>
                          </div>
                        </div>

                        {/* Adjustments */}
                        <div className="space-y-1">
                          {transferPreview.adjustments.creditAmount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Credit Applied:</span>
                              <span className="font-medium text-green-600">−₹{transferPreview.adjustments.creditAmount.toLocaleString()}</span>
                            </div>
                          )}
                          {transferPreview.adjustments.transferFee > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Extra to Pay (price diff):</span>
                              <span className="font-medium text-amber-600">+₹{transferPreview.adjustments.transferFee.toLocaleString()}</span>
                            </div>
                          )}
                          {transferPreview.adjustments.refundAmount > 0 && (
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">Cash Refund:</span>
                              <span className="font-medium text-green-600">₹{transferPreview.adjustments.refundAmount.toLocaleString()}</span>
                            </div>
                          )}
                          <div className="flex justify-between text-sm font-semibold pt-1 border-t border-amber-200">
                            <span>Net Amount Still to Pay:</span>
                            <span className={(transferPreview.summary.netAmountToPay ?? transferPreview.summary.netAmount ?? 0) > 0 ? 'text-amber-600' : 'text-green-600'}>
                              ₹{(transferPreview.summary.netAmountToPay ?? transferPreview.summary.netAmount ?? 0).toLocaleString()}
                            </span>
                          </div>
                        </div>

                        {transferPreview.adjustments.refundAmount > 0 && (
                          <div className="p-2 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                            <CheckCircleIcon className="h-3 w-3 inline mr-1" />
                            A refund of ₹{transferPreview.adjustments.refundAmount.toLocaleString()} will be processed separately.
                          </div>
                        )}
                        {transferPreview.adjustments.creditAmount > 0 && (
                          <div className="p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                            <CheckCircleIcon className="h-3 w-3 inline mr-1" />
                            ₹{transferPreview.adjustments.creditAmount.toLocaleString()} from prior payments is automatically credited to the new invoice.
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Transfer Reason */}
                <div className="space-y-2">
                  <Label htmlFor="transferReason">Transfer Reason *</Label>
                  <Textarea
                    id="transferReason"
                    value={transferData.transferReason}
                    onChange={(e) => setTransferData({ ...transferData, transferReason: e.target.value })}
                    placeholder="Please provide reason for transfer (e.g., student request, batch timing conflict, course change)"
                    rows={3}
                    required
                  />
                </div>

                {/* Additional Notes */}
                <div className="space-y-2">
                  <Label htmlFor="notes">Additional Notes (Optional)</Label>
                  <Textarea
                    id="notes"
                    value={transferData.notes}
                    onChange={(e) => setTransferData({ ...transferData, notes: e.target.value })}
                    placeholder="Any additional information about this transfer"
                    rows={2}
                  />
                </div>

                {/* Transfer Summary */}
                {transferData.toCourseId && transferData.toBatchId && (() => {
                  // Fee numbers: prefer server preview, fall back to local estimate
                  const currentInv = transferHistory?.currentEnrollment?.invoice;
                  const creditAmt  = transferPreview
                    ? (transferPreview.summary?.creditApplied ?? 0)
                    : (transferData.feeAdjustmentType === 'full_refund'
                        ? 0
                        : (currentInv?.paidAmount ?? 0));
                  const refundAmt  = transferPreview
                    ? (transferPreview.summary?.refundDue ?? 0)
                    : (transferData.feeAdjustmentType === 'full_refund'
                        ? (currentInv?.paidAmount ?? 0)
                        : 0);
                  // Net = new course fee minus whatever is credited from prior payments.
                  // extraFee (course price diff) is already embedded in transferApplicableFee,
                  // so we must NOT add it again.
                  const netToPay = transferPreview
                    ? (transferPreview.summary?.netAmountToPay ?? 0)
                    : Math.max(0, transferApplicableFee - creditAmt);
                  const perInst  = Number(transferData.numInstallments) > 1
                    ? Math.ceil(netToPay / Number(transferData.numInstallments))
                    : 0;

                  return (
                    <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <h4 className="font-semibold text-sm mb-3 text-blue-900 flex items-center gap-2">
                        <ArrowPathIcon className="h-4 w-4" />
                        Transfer Summary
                      </h4>
                      <div className="space-y-1.5 text-sm">
                        {/* Basic info */}
                        <div className="flex justify-between">
                          <span className="text-gray-600">Student:</span>
                          <span className="font-medium">{selectedStudent?.name}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Destination Course:</span>
                          <span className="font-medium">{transferSelectedCourse?.courseName}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Destination Batch:</span>
                          <span className="font-medium">{transferSelectedBatch?.name} ({transferSelectedBatch?.mode})</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Payment Plan:</span>
                          <span className="font-medium">
                            {Number(transferData.numInstallments) === 1
                              ? 'Full Payment'
                              : `EMI — ${transferData.numInstallments} installments`}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Fee Adjustment:</span>
                          <span className="font-medium capitalize">
                            {transferData.feeAdjustmentType.replace(/_/g, ' ')}
                          </span>
                        </div>

                        {/* Fee breakdown divider */}
                        <div className="border-t border-blue-200 pt-2 mt-2 space-y-1.5">
                          <div className="flex justify-between">
                            <span className="text-gray-600">New Course Fee:</span>
                            <span className="font-semibold text-blue-700">₹{transferApplicableFee.toLocaleString('en-IN')}</span>
                          </div>

                          {creditAmt > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Prior Payments Credited:</span>
                              <span className="font-medium text-green-700">− ₹{creditAmt.toLocaleString('en-IN')}</span>
                            </div>
                          )}

                          {refundAmt > 0 && (
                            <div className="flex justify-between">
                              <span className="text-gray-600">Refund Due to Student:</span>
                              <span className="font-medium text-emerald-700">₹{refundAmt.toLocaleString('en-IN')}</span>
                            </div>
                          )}

                          {/* Net remaining */}
                          <div className="flex justify-between items-center pt-1.5 border-t border-blue-300">
                            <span className="font-semibold text-gray-800">Remaining to Pay:</span>
                            <span className={`text-base font-bold ${netToPay > 0 ? 'text-red-600' : 'text-green-600'}`}>
                              ₹{netToPay.toLocaleString('en-IN')}
                            </span>
                          </div>

                          {perInst > 0 && (
                            <div className="flex justify-between text-xs text-gray-500">
                              <span>Per installment ({transferData.numInstallments}×):</span>
                              <span>≈ ₹{perInst.toLocaleString('en-IN')}</span>
                            </div>
                          )}
                        </div>

                        {!transferPreview && currentInv && (
                          <p className="text-xs text-gray-400 italic mt-1">
                            * Estimate based on current invoice. Click "Preview Fee Breakdown" for exact figures.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <DialogFooter className="mt-6">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsTransferDialogOpen(false);
                    setTransferData({
                      fromBatchId:       '',
                      toCourseId:        '',
                      toBatchId:         '',
                      transferReason:    '',
                      feeAdjustmentType: 'transfer_fee',
                      numInstallments:   1,
                      notes:             ''
                    });
                    setTransferPreview(null);
                    setTransferAvailableBatches([]);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  data-testid="transfer-student-submit"
                  type="submit"
                  disabled={!transferData.toCourseId || !transferData.toBatchId || !transferData.transferReason || transferLoading}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white"
                >
                  {transferLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Transferring...
                    </>
                  ) : (
                    'Confirm Transfer'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      {/* ── Discontinue Student Dialog ─────────────────────────────────────── */}
      <Dialog open={isDiscontinueDialogOpen} onOpenChange={setIsDiscontinueDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <NoSymbolIcon className="h-5 w-5" />
              Discontinue Student — {selectedStudent?.name}
            </DialogTitle>
            <DialogDescription>
              Mark this student as discontinued from their current course. A confirmation email and PDF report will be generated.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleDiscontinueSubmit} className="space-y-5 mt-2">

            {/* Course / batch selector */}
            <div className="space-y-1">
              <Label>Course / Batch to Discontinue <span className="text-red-500">*</span></Label>
              <Select
                value={discontinueData.batchId}
                onValueChange={(val) => {
                  setDiscontinueData(prev => ({ ...prev, batchId: val }));
                  fetchDiscontinuePreview(val, discontinueData.refundType);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select active enrollment" />
                </SelectTrigger>
                <SelectContent>
                  {(selectedStudent?.enrolledCourses || [])
                    .filter(c => !c.assignmentStatus || c.assignmentStatus === 'active')
                    .map(c => (
                      <SelectItem key={c.batchId} value={c.batchId}>
                        {c.courseName} — {c.batchName || c.batchId}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            {/* Fee preview */}
            {discontinuePreviewLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400" />
                Loading fee info…
              </div>
            )}
            {discontinuePreview && !discontinuePreviewLoading && (
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Total Fee',   value: discontinuePreview.totalFee,     color: 'text-gray-700' },
                  { label: 'Total Paid',  value: discontinuePreview.totalPaid,    color: 'text-green-600' },
                  { label: 'Pending',     value: discontinuePreview.pendingAmount, color: 'text-orange-600' }
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 border rounded-lg p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className={`text-base font-bold ${item.color}`}>₹{(item.value||0).toLocaleString()}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reason */}
            <div className="space-y-1">
              <Label>Reason for Discontinuation <span className="text-red-500">*</span></Label>
              <textarea
                className="w-full border rounded-md px-3 py-2 text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="E.g. Personal reasons, financial constraints, relocation…"
                value={discontinueData.reason}
                onChange={e => setDiscontinueData(prev => ({ ...prev, reason: e.target.value }))}
              />
            </div>

            {/* Refund type */}
            <div className="space-y-1">
              <Label>Refund Policy</Label>
              <Select
                value={discontinueData.refundType}
                onValueChange={(val) => {
                  setDiscontinueData(prev => ({
                    ...prev,
                    refundType: val,
                    refundAmount: '',
                    refundMode: 'cash',
                    referenceNo: ''
                  }));
                  if (discontinueData.batchId) fetchDiscontinuePreview(discontinueData.batchId, val);
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_refund">No Refund</SelectItem>
                  <SelectItem value="partial_refund">Partial Refund (prorated)</SelectItem>
                  <SelectItem value="full_refund">Full Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Refund details block */}
            {discontinueData.refundType !== 'no_refund' && (
              <div className="border border-amber-200 bg-amber-50 rounded-lg p-4 space-y-4">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Refund Details</p>

                {/* Amount */}
                <div className="space-y-1">
                  <Label>Refund Amount (₹) <span className="text-red-500">*</span></Label>
                  <Input
                    type="number"
                    min="1"
                    max={discontinuePreview?.totalPaid || 99999}
                    placeholder={discontinuePreview ? `Suggested: ₹${discontinuePreview.suggestedRefund}` : 'Enter refund amount'}
                    value={discontinueData.refundAmount}
                    onChange={e => setDiscontinueData(prev => ({ ...prev, refundAmount: e.target.value }))}
                    className="bg-white"
                  />
                  {discontinuePreview && (
                    <p className="text-xs text-amber-600">
                      Max refundable: ₹{(discontinuePreview.totalPaid||0).toLocaleString()}
                      {discontinuePreview.suggestedRefund > 0 && (
                        <button
                          type="button"
                          className="ml-2 underline"
                          onClick={() => setDiscontinueData(prev => ({ ...prev, refundAmount: discontinuePreview.suggestedRefund }))}
                        >
                          Use suggested (₹{discontinuePreview.suggestedRefund.toLocaleString()})
                        </button>
                      )}
                    </p>
                  )}
                </div>

                {/* Refund mode */}
                <div className="space-y-1">
                  <Label>Refund Mode <span className="text-red-500">*</span></Label>
                  <Select
                    value={discontinueData.refundMode}
                    onValueChange={val => setDiscontinueData(prev => ({ ...prev, refundMode: val }))}
                  >
                    <SelectTrigger className="bg-white">
                      <SelectValue placeholder="How will the refund be issued?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Reference number */}
                <div className="space-y-1">
                  <Label>Reference / Transaction No. <span className="text-gray-400 text-xs">(optional)</span></Label>
                  <Input
                    placeholder="UTR / cheque no. / UPI ref / voucher no."
                    value={discontinueData.referenceNo}
                    onChange={e => setDiscontinueData(prev => ({ ...prev, referenceNo: e.target.value }))}
                    className="bg-white"
                  />
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-1">
              <Label>Internal Notes</Label>
              <textarea
                className="w-full border rounded-md px-3 py-2 text-sm min-h-[60px] resize-none focus:outline-none focus:ring-2 focus:ring-red-300"
                placeholder="Any additional notes (not sent to student)"
                value={discontinueData.notes}
                onChange={e => setDiscontinueData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            {/* Info box */}
            <div className="bg-red-50 border border-red-200 rounded-md p-3 text-xs text-red-700 space-y-1">
              <p className="font-semibold">⚠ This action will:</p>
              <ul className="list-disc pl-4 space-y-0.5">
                <li>Mark the enrollment as <strong>Discontinued</strong></li>
                <li>Cancel all pending fee installments</li>
                <li>Send a confirmation email to the student</li>
                <li>Generate a PDF discontinuation report</li>
              </ul>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setIsDiscontinueDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={!discontinueData.batchId || !discontinueData.reason.trim() || discontinueLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {discontinueLoading ? (
                  <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />Processing…</>
                ) : (
                  <><XCircleIcon className="h-4 w-4 mr-1" />Confirm Discontinuation</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ══════════════════════════════════════════════════════════
          STUDENT HISTORY DIALOG
      ══════════════════════════════════════════════════════════ */}
      <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <DialogContent hideCloseButton className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col p-0">

          {/* ── Dialog Header ── */}
          <div className="bg-gradient-to-r from-violet-600 to-violet-700 text-white px-6 py-5 flex items-center gap-4 flex-shrink-0">
            <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center text-xl font-bold flex-shrink-0">
              {historyStudent?.name?.charAt(0)?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-bold truncate">{historyStudent?.name}</h2>
                {historyStudent?.studentId && (
                  <span className="text-xs font-mono bg-white/20 text-white px-2 py-0.5 rounded-full">{historyStudent.studentId}</span>
                )}
              </div>
              <p className="text-violet-200 text-sm">
                {historyStudent?.email}
                {historyStudent?.phone && <> · {historyStudent.phone}</>}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="bg-white/10 border-white/30 text-white hover:bg-white/20"
                onClick={() => { setIsHistoryDialogOpen(false); navigate(`/students/${historyStudent?._id}`); }}
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-1.5" />
                Full Profile
              </Button>
              <button
                onClick={() => setIsHistoryDialogOpen(false)}
                className="rounded-full p-1.5 text-white/70 hover:text-white hover:bg-white/20 transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {historyLoading ? (
            <div className="flex-1 flex items-center justify-center py-20">
              <div className="text-center text-gray-400">
                <div className="h-8 w-8 rounded-full border-4 border-violet-200 border-t-violet-600 animate-spin mx-auto mb-3" />
                <p className="text-sm">Loading student history…</p>
              </div>
            </div>
          ) : (
            <HistoryDialogBody
              student={historyStudent}
              historyData={historyData}
              historyTab={historyTab}
              setHistoryTab={setHistoryTab}
            />
          )}
        </DialogContent>
      </Dialog>

      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// HISTORY DIALOG BODY — extracted to avoid long JSX inline
// ────────────────────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  present:   'bg-emerald-100 text-emerald-700',
  absent:    'bg-red-100 text-red-700',
  late:      'bg-amber-100 text-amber-700',
  'half-day':'bg-blue-100 text-blue-700',
  excused:   'bg-violet-100 text-violet-700',
  holiday:   'bg-gray-100 text-gray-500',
};
const fmtDate  = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const fmtMoney = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

function HistoryDialogBody({ student, historyData, historyTab, setHistoryTab }) {
  const { invoices = [], attendances = [], percentByBatch = [] } = historyData;

  // ── Derived stats ──
  const activeInvoices = invoices.filter(i => i.status !== 'transferred');
  const totalFee   = activeInvoices.reduce((s, i) => s + (i.totalAmount || 0), 0);
  // Derive paid from installments (more reliable than invoice-level paidAmount field)
  const getInvPaid = (inv) => {
    if (inv.installments?.length) {
      const instSum = inv.installments.reduce((s, inst) => s + (inst.paidAmount || 0), 0);
      return Math.max(instSum, inv.paidAmount || 0);
    }
    return inv.paidAmount || 0;
  };
  const totalPaid  = activeInvoices.reduce((s, i) => s + getInvPaid(i), 0);
  const pending    = Math.max(0, totalFee - totalPaid);

  const totalSessions = attendances.length;
  const presentCount  = attendances.filter(a => a.status === 'present' || a.status === 'late').length;
  const absentCount   = attendances.filter(a => a.status === 'absent').length;
  const attRate       = totalSessions > 0 ? Math.round((presentCount / totalSessions) * 100) : 0;

  // ── Extract individual payment records from invoice installments ──
  // Each installment may have a `payments` sub-array (one entry per partial/full payment)
  // AND direct fields: installmentNo, paidAmount, status
  const payments = [];
  for (const inv of activeInvoices) {
    for (const inst of inv.installments || []) {
      const instNo     = inst.installmentNo ?? inst.installmentNumber ?? '—';
      const courseName = inv.courseId?.name || inv.courseName || '—';
      const batchName  = inv.batchId?.name  || inv.batchName  || '—';

      if (Array.isArray(inst.payments) && inst.payments.length > 0) {
        // Expand each sub-payment record
        for (const p of inst.payments) {
          payments.push({
            date:          p.paidDate || p.date,
            receiptNo:     p.receiptNo || '—',
            amount:        p.amount || 0,
            mode:          p.paymentMethod || p.mode || '—',
            installmentNo: instNo,
            courseName,
            batchName,
            status:        inst.status,
          });
        }
      } else if ((inst.paidAmount || 0) > 0) {
        // Legacy / fallback: use top-level fields if sub-array is empty
        payments.push({
          date:          inst.paidDate || inst.updatedAt,
          receiptNo:     inst.receiptNo || '—',
          amount:        inst.paidAmount,
          mode:          inst.paymentMethod || inst.mode || '—',
          installmentNo: instNo,
          courseName,
          batchName,
          status:        inst.status,
        });
      }
    }
  }
  payments.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

  const miniStat = (label, value, colorClass) => (
    <div className="bg-white border rounded-xl p-4 text-center shadow-sm">
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <p className={`text-2xl font-bold ${colorClass}`}>{value}</p>
    </div>
  );

  return (
    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 bg-gray-50/50">
      {/* ── Overview stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {miniStat('Total Fee',         fmtMoney(totalFee),      'text-gray-700')}
        {miniStat('Total Paid',        fmtMoney(totalPaid),     'text-emerald-600')}
        {miniStat('Outstanding',       fmtMoney(pending),       pending > 0 ? 'text-red-600' : 'text-gray-400')}
        {miniStat('Attendance Rate',   `${attRate}%`,           attRate >= 75 ? 'text-emerald-600' : attRate >= 50 ? 'text-amber-600' : 'text-red-600')}
      </div>

      {/* ── Attendance quick chips ── */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: `${presentCount} Present`,   cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
          { label: `${absentCount} Absent`,     cls: 'bg-red-50 text-red-700 border-red-200' },
          { label: `${attendances.filter(a=>a.status==='late').length} Late`,      cls: 'bg-amber-50 text-amber-700 border-amber-200' },
          { label: `${attendances.filter(a=>a.status==='excused').length} Excused`, cls: 'bg-violet-50 text-violet-700 border-violet-200' },
          { label: `${totalSessions} Sessions`, cls: 'bg-gray-50 text-gray-600 border-gray-200' },
        ].map(c => (
          <span key={c.label} className={`text-xs font-medium px-2.5 py-1 rounded-full border ${c.cls}`}>{c.label}</span>
        ))}
      </div>

      {/* ── Tabs ── */}
      <Tabs value={historyTab} onValueChange={setHistoryTab}>
        <TabsList className="bg-white border shadow-sm">
          <TabsTrigger value="overview"    className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
            <ChartBarIcon className="h-3.5 w-3.5 mr-1" /> Enrollments
          </TabsTrigger>
          <TabsTrigger value="payments"    className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
            <BanknotesIcon className="h-3.5 w-3.5 mr-1" /> Fee Payments
            {payments.length > 0 && <span className="ml-1 bg-violet-100 text-violet-700 rounded-full px-1.5 text-[10px] font-bold">{payments.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="attendance"  className="data-[state=active]:bg-violet-600 data-[state=active]:text-white">
            <CalendarIcon className="h-3.5 w-3.5 mr-1" /> Attendance
            {totalSessions > 0 && <span className="ml-1 bg-violet-100 text-violet-700 rounded-full px-1.5 text-[10px] font-bold">{totalSessions}</span>}
          </TabsTrigger>
        </TabsList>

        {/* ── TAB: Enrollments ── */}
        <TabsContent value="overview" className="mt-3">
          {invoices.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No enrollment records found</div>
          ) : (
            <div className="space-y-3">
              {invoices.map(inv => {
                const paid    = getInvPaid(inv);
                const total   = inv.totalAmount || 0;
                const pct     = total > 0 ? Math.round((paid / total) * 100) : 0;
                const isTransferred = inv.status === 'transferred';
                return (
                  <div key={inv._id} className={`bg-white border rounded-xl p-4 shadow-sm ${isTransferred ? 'opacity-60' : ''}`}>
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <p className="font-semibold text-gray-800 text-sm">{inv.courseId?.name || inv.courseName || 'Course'}</p>
                        <p className="text-xs text-gray-400">{inv.batchId?.name || inv.batchName || 'Batch'}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        inv.status === 'paid'         ? 'bg-emerald-100 text-emerald-700' :
                        inv.status === 'partially_paid'? 'bg-amber-100 text-amber-700' :
                        inv.status === 'transferred'  ? 'bg-blue-100 text-blue-700' :
                        inv.status === 'discontinued' ? 'bg-red-100 text-red-700' :
                                                        'bg-gray-100 text-gray-500'
                      }`}>
                        {isTransferred ? 'Transferred' : inv.status?.replace('_', ' ') || 'active'}
                      </span>
                    </div>
                    {!isTransferred && (
                      <>
                        <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
                          <span>Total: <strong className="text-gray-700">{fmtMoney(total)}</strong></span>
                          <span>Paid: <strong className="text-emerald-600">{fmtMoney(paid)}</strong></span>
                          <span>Pending: <strong className={total - paid > 0 ? 'text-red-600' : 'text-gray-400'}>{fmtMoney(Math.max(0, total - paid))}</strong></span>
                        </div>
                        <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${pct}%` }} />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">{pct}% paid</p>

                        {/* Installment breakdown */}
                        {inv.installments?.length > 1 && (
                          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {inv.installments.map((inst, i) => (
                              <div key={i} className="bg-gray-50 rounded-lg p-2 text-xs border">
                                <p className="text-gray-500">Installment {inst.installmentNumber || i + 1}</p>
                                <p className="font-semibold text-gray-700">{fmtMoney(inst.amount)}</p>
                                <p className={`font-medium ${inst.status === 'paid' ? 'text-emerald-600' : inst.status === 'partially_paid' ? 'text-amber-600' : 'text-gray-400'}`}>
                                  {inst.status?.replace('_', ' ') || 'pending'}
                                  {inst.paidDate && <> · {fmtDate(inst.paidDate)}</>}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ── TAB: Fee Payments ── */}
        <TabsContent value="payments" className="mt-3">
          {payments.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No payment records found</div>
          ) : (
            <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-violet-600 hover:bg-violet-600">
                    <TableHead className="text-xs font-semibold text-white">Date</TableHead>
                    <TableHead className="text-xs font-semibold text-white">Receipt No</TableHead>
                    <TableHead className="text-xs font-semibold text-white">Course / Batch</TableHead>
                    <TableHead className="text-xs font-semibold text-white">Installment</TableHead>
                    <TableHead className="text-xs font-semibold text-white">Mode</TableHead>
                    <TableHead className="text-right text-xs font-semibold text-white">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((p, i) => (
                    <TableRow key={i} className="hover:bg-violet-50/30">
                      <TableCell className="text-xs py-2.5">{fmtDate(p.date)}</TableCell>
                      <TableCell className="text-xs py-2.5 font-mono text-violet-700">{p.receiptNo}</TableCell>
                      <TableCell className="text-xs py-2.5">
                        <p className="font-medium text-gray-800">{p.courseName}</p>
                        <p className="text-gray-400">{p.batchName}</p>
                      </TableCell>
                      <TableCell className="text-xs py-2.5 text-gray-500">#{p.installmentNo}</TableCell>
                      <TableCell className="text-xs py-2.5 capitalize text-gray-500">{p.mode}</TableCell>
                      <TableCell className="text-right text-xs py-2.5 font-semibold text-emerald-600">{fmtMoney(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="px-4 py-3 border-t bg-gray-50 flex justify-between text-xs text-gray-500">
                <span>{payments.length} payment{payments.length !== 1 ? 's' : ''}</span>
                <span className="font-bold text-emerald-700">Total: {fmtMoney(payments.reduce((s, p) => s + (p.amount || 0), 0))}</span>
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── TAB: Attendance ── */}
        <TabsContent value="attendance" className="mt-3 space-y-3">
          {/* Per-batch stats */}
          {percentByBatch.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {percentByBatch.map((b, i) => {
                const pct = Math.round(b.percentage || 0);
                return (
                  <div key={i} className="bg-white border rounded-xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-sm font-semibold text-gray-800">{b.batchName || '—'}</p>
                        <p className="text-xs text-gray-400">{b.batchCode}</p>
                      </div>
                      <span className={`text-xl font-bold ${pct >= 75 ? 'text-emerald-600' : pct >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{pct}%</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all ${pct >= 75 ? 'bg-emerald-500' : pct >= 50 ? 'bg-amber-500' : 'bg-red-500'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="text-[10px] text-gray-400 mt-1">{b.present} present out of {b.total} sessions</p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Records list */}
          {attendances.length === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm">No attendance records found</div>
          ) : (
            <div className="bg-white border rounded-xl overflow-hidden shadow-sm">
              <div className="px-4 py-2.5 border-b bg-gray-50 text-xs font-semibold text-gray-500">
                Recent Attendance Records ({attendances.length} total)
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {attendances.slice(0, 100).map(a => (
                  <div key={a._id} className="flex items-center justify-between px-4 py-2.5">
                    <div className="flex items-center gap-3">
                      <div className="text-xs text-gray-700 font-medium w-28 flex-shrink-0">{fmtDate(a.date)}</div>
                      <div className="text-xs text-gray-400">{a.batchId?.name || '—'}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {(a.reason || a.notes) && (
                        <span className="text-[10px] text-gray-400 max-w-[120px] truncate">{a.reason || a.notes}</span>
                      )}
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium capitalize ${STATUS_COLORS[a.status] || 'bg-gray-100 text-gray-500'}`}>
                        {a.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
              {attendances.length > 100 && (
                <div className="px-4 py-2.5 border-t text-xs text-gray-400 text-center bg-gray-50">
                  Showing 100 of {attendances.length} records · Open Full Profile for complete history
                </div>
              )}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
